/**
 * VaultifyPickers — variant picker framework
 *
 * Provides a registry, shared BasePicker base class, and picker implementations.
 * Each picker type is a class extending BasePicker, registered with
 * VaultifyPickers.register(). New picker types follow the same pattern —
 * extend, implement _init() / _destroy(), call VaultifyPickers.register().
 *
 * Journey mode:
 * A JourneyOrchestrator class will manage multi-step variant selection when
 * data-journey="true" is present on <variant-selects>. It will wrap picker
 * instances for a product, control step visibility, advance on selection,
 * and surface a summary before add-to-cart. The hook points are already in
 * place — see the JourneyOrchestrator stub at the bottom of this file.
 *
 * Dawn integration:
 * All picker types keep a hidden <select class="select__select"> in sync so
 * Dawn's VariantSelects custom element continues to own variant resolution.
 * Change events are dispatched with _vaultifyInternal = true to pass the
 * isTrusted guard in global.js (see Knowledge Log 2026-07-03).
 */
(function () {
  'use strict';

  // ── Framework ──────────────────────────────────────────────────────────────

  const _pickerRegistry = new Map(); // type → PickerClass
  const _orchestratorRegistry = new Map(); // mode → OrchestratorClass
  let _waitlistProvider = null; // registered by a provider adapter (e.g. stoq-waitlist-adapter.js)

  const VaultifyPickers = {
    /**
     * Register a picker type.
     * The class must set a static `selector` property identifying its root element.
     */
    register(type, PickerClass) {
      _pickerRegistry.set(type, PickerClass);
    },

    /**
     * Register an orchestrator mode (e.g. 'journey').
     * Called after pickers are initialised; receives the variant-selects element
     * and the map of initialised picker instances within it.
     */
    registerOrchestrator(mode, OrchestratorClass) {
      _orchestratorRegistry.set(mode, OrchestratorClass);
    },

    /**
     * Register the active waitlist provider adapter. Exactly one provider is
     * expected at a time — a later registration replaces an earlier one.
     * Keeps ProductInteractionState (and any future picker renderer) free of
     * provider-specific names: it only ever calls provider.isReady()/.submit().
     * @param {{ name: string, isReady: () => boolean, submit: (variantIds: string[], context: object) => Promise }} provider
     */
    registerWaitlistProvider(provider) {
      _waitlistProvider = provider;
    },

    /** Returns the currently registered waitlist provider, or null if none. */
    getWaitlistProvider() {
      return _waitlistProvider;
    },

    /**
     * Returns the ProductInteractionState instance owning a given element's
     * product component, or null if none exists (e.g. product with only a
     * default variant, which renders no <variant-selects> at all).
     */
    getInteractionState(el) {
      return el?.closest('variant-selects')?._vaultifyInteractionState || null;
    },

    /** Initialise all registered picker types. Skips already-initialised elements. */
    init(root) {
      root = root || document;
      this._initInteractionStates(root);
      _pickerRegistry.forEach((PickerClass) => {
        root.querySelectorAll(PickerClass.selector).forEach((el) => {
          if (!el._vaultifyPicker) {
            el._vaultifyPicker = new PickerClass(el);
          }
        });
      });
      this._initOrchestrators(root);
    },

    /** Re-initialise after a Dawn section fetch replaces the picker DOM. */
    reinit(root) {
      root = root || document;
      this._initInteractionStates(root);
      _pickerRegistry.forEach((PickerClass) => {
        root.querySelectorAll(PickerClass.selector).forEach((el) => {
          if (el._vaultifyPicker) el._vaultifyPicker.destroy();
          el._vaultifyPicker = new PickerClass(el);
        });
      });
      this._initOrchestrators(root);
    },

    // Instantiated before pickers so picker renderers (in a later phase) can
    // query an already-existing interaction state at construction time.
    // Destroyed and recreated on every reinit — same as orchestrators — but
    // the waitlist queue itself is anchored to the variant-selects element,
    // not the instance, so it survives this recreation (see
    // ProductInteractionState for the reasoning).
    _initInteractionStates(root) {
      root.querySelectorAll('variant-selects').forEach((vs) => {
        if (!vs.querySelector('[data-product-interaction-context]')) return;
        vs._vaultifyInteractionState?.destroy?.();
        vs._vaultifyInteractionState = new ProductInteractionState(vs);
      });
    },

    _initOrchestrators(root) {
      if (!_orchestratorRegistry.size) return;
      root.querySelectorAll('variant-selects[data-picker-mode]').forEach((vs) => {
        vs._vaultifyOrchestrator?.destroy?.();
        const mode = vs.dataset.pickerMode;
        const Klass = _orchestratorRegistry.get(mode);
        if (!Klass) return;
        const pickers = new Map();
        vs.querySelectorAll('[data-picker-type]').forEach((el) => {
          if (el._vaultifyPicker) pickers.set(el.dataset.pickerType, el._vaultifyPicker);
        });
        vs._vaultifyOrchestrator = new Klass(vs, pickers);
      });
    },
  };

  window.VaultifyPickers = VaultifyPickers;

  // ── ProductInteractionState ─────────────────────────────────────────────────
  // One instance per <variant-selects>. Owns:
  //   - the trusted scoped product/customer context (read once, never window.productData)
  //   - exact-variant resolution (delegates the pure logic to VaultifyWaitlistCore)
  //   - provider-independent Purchase State
  //   - the waitlist queue (exact variant IDs only)
  // Does NOT own price, media, or cart rendering — those remain Dawn's
  // section-fetch pipeline, untouched. Does NOT know STOQ — waitlist
  // capability and submission go through VaultifyPickers.getWaitlistProvider().
  //
  // Picker renderers (a later phase) consult this via
  // VaultifyPickers.getInteractionState(el) rather than reading picker DOM
  // themselves — this keeps business logic out of picker classes entirely.

  class ProductInteractionState {
    constructor(root) {
      this.root = root; // the <variant-selects> element
      this._context = this._readContext();

      // Waitlist queue backing store lives on the variant-selects element
      // itself, not on this instance — this instance is destroyed and
      // recreated on every reinit() (same as pickers/orchestrators), but
      // variant-selects persists across a Dawn section fetch (only its
      // innerHTML is replaced — see the JourneyOrchestrator _journeyAllDone
      // precedent for the same pattern). Anchoring the Set to the element
      // means: internal refreshes preserve it for free, a product swap or
      // Quick Add close destroys the element (and the Set with it) for
      // free, and reopening Quick Add creates a fresh element (and empty
      // Set) for free — no lifecycle branching needed for any of these
      // cases. This property is intentionally not part of the public API;
      // nothing outside the methods below should read or write it.
      if (!(root.__vaultifyWaitlistStore instanceof Set)) {
        root.__vaultifyWaitlistStore = new Set();
      }
      this._queue = VaultifyWaitlistCore.createWaitlistQueue(root.__vaultifyWaitlistStore);

      // Successfully-submitted variant IDs — a second, separate collection
      // from the pending queue, anchored to the same element for exactly
      // the same lifecycle reasons (see above): survives internal refreshes,
      // destroyed with the element on product swap or Quick Add close,
      // fresh on Quick Add reopen. Deliberately NOT pruned by axis changes
      // the way the pending queue is — "submitted this session" is a fact
      // about what the customer already did, not about what's currently
      // selected, so it should outlive the customer browsing away from and
      // back to a given combination within the same product interaction.
      if (!(root.__vaultifyWaitlistSubmittedStore instanceof Set)) {
        root.__vaultifyWaitlistSubmittedStore = new Set();
      }
      this._submitted = VaultifyWaitlistCore.createWaitlistQueue(root.__vaultifyWaitlistSubmittedStore);

      // Prune on construction (i.e. on every reinit/variantChange) so a
      // queue entry invalidated by an axis change elsewhere in the journey
      // never lingers past the refresh that invalidated it. Only the
      // pending queue is pruned this way — see above.
      if (this._context) this._queue.prune(this._currentSelections(), this._context.variants);
    }

    destroy() {
      // No listeners of our own to remove yet (this phase adds no DOM
      // rendering) — present for symmetry with other picker/orchestrator
      // destroy() methods and so a later phase has an obvious place to
      // add cleanup without restructuring the lifecycle.
    }

    _readContext() {
      const el = this.root.querySelector('[data-product-interaction-context]');
      if (!el) return null;
      try {
        const parsed = JSON.parse(el.textContent);
        return {
          productId: parsed.productId,
          productTitle: parsed.productTitle,
          vendor: parsed.vendor,
          shopDomain: parsed.shopDomain,
          variants: Array.isArray(parsed.variants) ? parsed.variants : [],
          customer: parsed.customer || null,
        };
      } catch (_err) {
        return null;
      }
    }

    /** Current selected value for every fieldset/select, indexed by option position. */
    _currentSelections() {
      return Array.from(this.root.querySelectorAll('fieldset, select.select__select')).map((fieldset) => {
        if (fieldset.tagName === 'SELECT') return fieldset.value;
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
        const sel = fieldset.querySelector('select');
        return sel ? sel.value : '';
      });
    }

    /**
     * Resolves a single row candidate: one axis, one value, evaluated
     * against every other axis's current selection.
     * @returns {{state: string, variant: object|null}}
     */
    resolveCandidate(axisIndex, value) {
      if (!this._context) return { state: 'invalid', variant: null };
      return VaultifyWaitlistCore.resolveVariantCandidate(
        axisIndex,
        value,
        this._currentSelections(),
        this._context.variants,
      );
    }

    /** Resolves the variant matching the complete current selection set. */
    getResolvedVariant() {
      if (!this._context) return null;
      const result = VaultifyWaitlistCore.resolveFromSelections(this._currentSelections(), this._context.variants);
      return result.variant;
    }

    /**
     * Looks up a variant by exact Shopify variant ID from the trusted
     * array. Added for the journey selection summary (JourneyOrchestrator):
     * unlike getResolvedVariant(), which recomputes live from
     * _currentSelections() and can therefore reflect an in-progress
     * EXPLORATION (a radio checked without a 'change' event — see the
     * journey branch exploration architecture doc, §15), this looks up
     * whatever variant ID Dawn's own hidden `input[name="id"]` currently
     * holds — updated only by a real, 'change'-driven section fetch — so
     * the summary always reflects the last genuine purchase selection,
     * never a branch the customer is merely browsing through.
     */
    getVariantById(variantId) {
      if (!this._context || variantId === undefined || variantId === null || variantId === '') return null;
      return this._context.variants.find((v) => String(v.id) === String(variantId)) || null;
    }

    /**
     * Provider-independent Purchase State for the current selection.
     * Reads the EXISTING preorder mechanism's own output (whether the
     * theme-owned .purchase-context[data-purchase-state="preorder"]
     * container has been un-hidden by togglePreorderDetails()) rather
     * than duplicating preorder detection — preorder always wins.
     */
    getPurchaseState() {
      if (!this._context) return VaultifyWaitlistCore.PURCHASE_STATE.INVALID;
      const selections = this._currentSelections();
      const result = VaultifyWaitlistCore.resolveFromSelections(selections, this._context.variants);
      return VaultifyWaitlistCore.computePurchaseState(result.state, {
        preorderActive: this._isPreorderActive(),
        waitlistCapable: this._isWaitlistCapable(),
      });
    }

    _isPreorderActive() {
      const productInfo = this.root.closest('product-info') || this.root.closest('[id^="MainProduct-"]');
      const el = productInfo?.querySelector('[data-purchase-state="preorder"]');
      return !!el && !el.hasAttribute('hidden');
    }

    _isWaitlistCapable() {
      const provider = VaultifyPickers.getWaitlistProvider();
      return !!provider && provider.isReady();
    }

    /**
     * Provider-independent Purchase State for a single row *candidate* (one
     * axis, one value) rather than the whole current selection — what a
     * picker row needs to decide whether it should offer a waitlist
     * affordance on that specific row, independent of what's selected
     * elsewhere. Added for Phase 2 (dropdown renderer): Phase 1 only needed
     * "purchase state for the current selection" (getPurchaseState()); a
     * picker rendering many rows at once needs it per-candidate. Reuses the
     * exact same resolveCandidate() + computePurchaseState() Phase 1 already
     * exposes — not a new resolution or state derivation, just applying the
     * existing ones to a candidate instead of the current selection.
     */
    getPurchaseStateForCandidate(axisIndex, value) {
      if (!this._context) return VaultifyWaitlistCore.PURCHASE_STATE.INVALID;
      const { state } = this.resolveCandidate(axisIndex, value);
      return VaultifyWaitlistCore.computePurchaseState(state, {
        preorderActive: this._isPreorderActive(),
        waitlistCapable: this._isWaitlistCapable(),
      });
    }

    /**
     * Classifies a journey BRANCH (one axis, one value) as purchasable,
     * waitlist-explorable, or unavailable (Phase X). Distinct from
     * getPurchaseStateForCandidate(), which resolves to exactly one variant
     * using every OTHER axis's current selection — a branch instead spans
     * every not-yet-chosen descendant beneath it, so only axes BEFORE
     * axisIndex constrain the match; axisIndex itself and every later axis
     * are wildcards. Reuses the same capability signals
     * (preorderActive/waitlistCapable) every other Purchase State method
     * here already uses — see classifyBranchState() in
     * vaultify-waitlist-core.js for the actual matching/classification.
     */
    getBranchState(axisIndex, value) {
      if (!this._context) return VaultifyWaitlistCore.BRANCH_STATE.UNAVAILABLE;
      return VaultifyWaitlistCore.classifyBranchState(
        axisIndex,
        value,
        this._currentSelections(),
        this._context.variants,
        { preorderActive: this._isPreorderActive(), waitlistCapable: this._isWaitlistCapable() },
      );
    }

    // ── Waitlist queue ─────────────────────────────────────────────────────
    // Encapsulated: no code outside these methods touches
    // root.__vaultifyWaitlistStore directly.

    getWaitlistVariantIds() {
      return this._queue.getAll();
    }

    isWatching(variantId) {
      return this._queue.has(variantId);
    }

    /**
     * Only adds when the candidate is genuinely resolved-sold-out, not
     * preorder-eligible, and not already submitted this session — never
     * option values or ambiguous rows. The preorder guard was added in
     * Phase 2: preorder always takes precedence over waitlist (see
     * getPurchaseState()/getPurchaseStateForCandidate()), so the queue
     * itself must never accept a preorder-eligible variant regardless of
     * which caller asks. The submitted guard was added alongside the
     * submitted-state collection below, for the same reason: a variant the
     * customer has already successfully signed up for this session must
     * never be queued again, even by a caller that bypasses the picker's
     * own row-state check — this is a queue-level data-integrity
     * guarantee, not merely a rendering decision left to the picker.
     */
    addWaitlistVariant(axisIndex, value) {
      const { state, variant } = this.resolveCandidate(axisIndex, value);
      if (state !== 'resolved-sold-out') return false;
      if (this._isPreorderActive()) return false;
      if (this._submitted.has(variant.id)) return false;
      return this._queue.add(variant.id);
    }

    removeWaitlistVariant(variantId) {
      return this._queue.remove(variantId);
    }

    /** Toggles by resolved candidate (mirrors addWaitlistVariant's eligibility checks) rather than a raw ID. */
    toggleWaitlistVariant(axisIndex, value) {
      const { state, variant } = this.resolveCandidate(axisIndex, value);
      if (state !== 'resolved-sold-out') return null;
      if (this._isPreorderActive()) return null;
      if (this._submitted.has(variant.id)) return null;
      return this._queue.toggle(variant.id);
    }

    /** Clears only the pending queue — submitted confirmations are a separate, deliberately longer-lived collection (see the constructor comment and getSubmittedWaitlistVariantIds()). */
    clearWaitlist() {
      this._queue.clear();
    }

    pruneWaitlist() {
      if (!this._context) return [];
      return this._queue.prune(this._currentSelections(), this._context.variants);
    }

    /** Future hydration hook — lets a later server-backed "already watching" response seed the queue. Never localStorage. */
    replaceWaitlistVariantIds(variantIds) {
      this._queue.replace(variantIds);
    }

    // ── Waitlist submitted state (current session only) ─────────────────────
    // A second, separate collection from the pending queue — see the
    // constructor comment for lifecycle/encapsulation reasoning. Represents
    // "successfully submitted this session", never "will still be true on
    // the customer's next visit" (that requires Phase 4's server-side
    // hydration, not implemented here).

    getSubmittedWaitlistVariantIds() {
      return this._submitted.getAll();
    }

    isWaitlistSubmitted(variantId) {
      return this._submitted.has(variantId);
    }

    /** Marks one or more variant IDs as submitted. Idempotent — marking an already-submitted ID again is a no-op. */
    markWaitlistSubmitted(variantIds) {
      (variantIds || []).forEach((variantId) => this._submitted.add(variantId));
    }

    removeSubmittedWaitlistVariant(variantId) {
      return this._submitted.remove(variantId);
    }

    clearSubmittedWaitlist() {
      this._submitted.clear();
    }

    getCustomerContext() {
      return this._context?.customer || null;
    }

    getProductContext() {
      if (!this._context) return null;
      // Field names match what StoqWaitlistAdapter's buildIntentPayload()
      // actually reads (product.id/product.title) — not this._context's
      // own productId/productTitle keys. Renaming here rather than in the
      // adapter, since { id, title } is the same shape variant objects
      // already use throughout this file (variant.id, variant.title).
      return {
        id: this._context.productId,
        title: this._context.productTitle,
        vendor: this._context.vendor,
        shopDomain: this._context.shopDomain,
      };
    }

    /**
     * Submits the current waitlist queue through the registered provider.
     * Added for Phase 2: submission needs the trusted variants array,
     * product context, and customer context together — all of which this
     * class already holds privately. Exposing them individually so a picker
     * could assemble a provider payload itself would mean either widening
     * the public surface to leak the trusted variants array (weakening the
     * "resolved variant must come from ProductInteractionState" boundary)
     * or duplicating this assembly in picker code. Owning submission here
     * keeps DropdownPicker (and any future picker) to exactly one call:
     * `await interactionState.submitWaitlist({ email })`.
     *
     * @param {{ email?: string }} [override] - guest email, or an override
     *   for a logged-in customer's account email (not currently exposed by
     *   any renderer in Phase 2 — see the architecture doc's "alternate
     *   email" note).
     * @returns {Promise<{succeeded: Array, failed: Array}>} provider-neutral result.
     */
    async submitWaitlist(override = {}) {
      const variantIds = this.getWaitlistVariantIds();
      if (!variantIds.length) return { succeeded: [], failed: [] };

      const provider = VaultifyPickers.getWaitlistProvider();
      if (!provider || !provider.isReady()) {
        return {
          succeeded: [],
          failed: variantIds.map((variantId) => ({ variantId, reason: 'no-provider', providerErrors: [] })),
        };
      }

      const accountCustomer = this._context?.customer;
      const email = override.email || accountCustomer?.email;
      if (!email) {
        return {
          succeeded: [],
          failed: variantIds.map((variantId) => ({ variantId, reason: 'missing-email', providerErrors: [] })),
        };
      }
      const customer = accountCustomer ? { ...accountCustomer, email } : { email };

      const result = await provider.submit(variantIds, {
        variants: this._context.variants,
        product: this.getProductContext(),
        shopDomain: this._context.shopDomain,
        customer,
      });

      // Successful IDs move from pending to submitted — never just removed.
      // Leaving them out of both collections would let the same variant be
      // queued and resubmitted; the submitted collection is what lets a row
      // render "Notification set" and stay non-toggleable for the rest of
      // this product interaction, per the required UX correction.
      //
      // Duplicate/"already subscribed" responses: STOQ's exact response
      // shape for this case is still unverified (see the architecture doc).
      // Once it can be safely classified, the adapter should normalise it
      // into `succeeded` — no change would be needed here, since it already
      // treats every succeeded ID this way. Not speculatively implemented.
      result.succeeded.forEach(({ variantId }) => {
        this._queue.remove(variantId);
        this._submitted.add(variantId);
      });
      return result;
    }
  }

  VaultifyPickers.ProductInteractionState = ProductInteractionState;

  // ── BasePicker ─────────────────────────────────────────────────────────────

  class BasePicker {
    /**
     * @param {HTMLElement} wrap - The picker's root element.
     */
    constructor(wrap) {
      this.wrap = wrap;
      this.selectEl = wrap.querySelector('select.select__select');
      this.optionIdx = parseInt(wrap.dataset.optionIndex, 10) || 0;
      this._init();
      // updateVariantOptionStates() bails early when no .variant-group fieldsets
      // exist (stop track, dropdown on single-option products). Remove the loading
      // class here so every VaultifyUK-managed picker is interactive after init.
      wrap.closest('variant-selects')?.classList.remove('variant-selects--loading');
    }

    /** Override in subclass to wire up the picker. */
    _init() {}

    /** Override in subclass to clean up listeners before reinit. */
    _destroy() {}

    destroy() {
      this._destroy();
    }

    /**
     * Sync the hidden <select> and dispatch a Dawn-compatible change event.
     * Uses _vaultifyInternal to pass the isTrusted guard in VariantSelects.
     */
    _dispatchChange(value) {
      const sel = this.selectEl;
      if (!sel) return;
      sel.value = value;
      Array.from(sel.options).forEach((opt) => {
        if (opt.value === value) opt.setAttribute('selected', 'selected');
        else opt.removeAttribute('selected');
      });
      const evt = new Event('change', { bubbles: true });
      evt._vaultifyInternal = true;
      sel.dispatchEvent(evt);
    }

    /**
     * Lowest price in pence for a given option value, sourced from
     * window.productData. Prefers available variants. Returns null when
     * productData is not set (T1 pages, non-product pages).
     */
    _getPriceCents(optionValue) {
      const pd = window.productData;
      if (!pd || !Array.isArray(pd.variants)) return null;
      const key = 'option' + (this.optionIdx + 1); // option1, option2, …
      const matches = pd.variants.filter((v) => v[key] === optionValue);
      if (!matches.length) return null;
      const pool = matches.filter((v) => v.available).length ? matches.filter((v) => v.available) : matches;
      return Math.min(...pool.map((v) => v.price));
    }

    _formatPrice(cents) {
      return '£' + (cents / 100).toFixed(2);
    }

    // ── Waitlist panel (shared across every picker type) ────────────────────
    // Everything below is renderer-agnostic: it reads this.interactionState,
    // this.waitlistPanelEl, and the optional this.waitlistControlsEl/
    // selectAllBtn/clearWaitlistBtn/waitlistSepEl (any picker type that
    // doesn't render those — e.g. ListPicker, which intentionally omits
    // Select all/Clear — simply leaves them null/undefined; every reference
    // below is already null-guarded). A subclass wires up these properties
    // in its own _init() and provides its own _render()/_toggleWaitlistRow()
    // for how its rows look and respond to clicks — this is the one
    // ProductInteractionState-driven panel implementation every picker
    // shares, not a template to copy.
    //
    // Originally written for DropdownPicker (Phase 2); promoted here
    // unchanged when ListPicker (Phase 3) needed the identical panel, per
    // "reuse the existing waitlist implementation... do not create another
    // waitlist panel implementation."

    // Every waitlist string display must go through this — reading
    // window.waitlistStrings?.key directly anywhere else re-introduces the
    // undecoded "&#39;" bug this exists to fix (found and fixed once
    // already at the panel heading; then found again, still present on
    // several other strings, including the error banner — see git history).
    _t(key, tokens, fallback = '') {
      // theme.liquid renders these through Shopify's `| t` filter, which
      // auto-escapes HTML entities (e.g. "We'll" -> "We&#39;ll") regardless
      // of the fact that this is a JS string context, not HTML. Every
      // caller assigns the result via .textContent (correct — never
      // innerHTML, no XSS risk here), which doesn't decode entities, so
      // without this the customer would see the literal "&#39;" text.
      // Decoding once here, in the one place every waitlist string is read,
      // covers every affected key without touching the locale files.
      const template = window.waitlistStrings?.[key] ?? fallback;
      const decoded = document.createElement('textarea');
      decoded.innerHTML = template;
      return VaultifyWaitlistCore.formatTemplate(decoded.value, tokens || {});
    }

    _pluralOptionNoun(count) {
      const noun = (this.optionName || 'option').toLowerCase();
      return count === 1 ? noun : VaultifyWaitlistCore.pluralizeNoun(noun);
    }

    /** Live-region announcement for AT users — row state changes, submission progress/result. */
    _announce(text) {
      if (!this.statusEl || !text) return;
      this.statusEl.textContent = '';
      requestAnimationFrame(() => {
        this.statusEl.textContent = text;
      });
    }

    // ── Waitlist: row eligibility ────────────────────────────────────────────
    // The single source of truth for "can this row be queued" — never
    // inferred from the server-rendered is-sold-out class alone (that class
    // reflects Shopify's general per-value availability, not an exact
    // resolved variant in the current selection context). Always asks
    // ProductInteractionState, which is the only thing that resolves
    // candidates and computes Purchase State.

    /**
     * Returns { eligible, pending, submitted, rowState, variant } — the
     * single source of truth for what a sold-out row can do and show.
     * `eligible` alone answers "does this resolve to exactly one WAITLIST
     * variant" (never inferred from the server-rendered is-sold-out class,
     * which only reflects Shopify's general per-value availability).
     * `pending`/`submitted` layer the customer's own current-session action
     * on top, via VaultifyWaitlistCore.resolveWaitlistRowState — a
     * *submitted* row is still `eligible` underneath (its stock condition
     * hasn't changed), but rowState reports 'submitted', not 'actionable'.
     */
    _rowWaitlistInfo(opt) {
      const empty = { eligible: false, pending: false, submitted: false, rowState: null, variant: null };
      if (!this.interactionState || opt.available) return empty;
      const { state, variant } = this.interactionState.resolveCandidate(this.optionIdx, opt.value);
      if (state !== 'resolved-sold-out') return empty;
      const purchaseState = this.interactionState.getPurchaseStateForCandidate(this.optionIdx, opt.value);
      const eligible = purchaseState === VaultifyWaitlistCore.PURCHASE_STATE.WAITLIST;
      if (!eligible) return { ...empty, variant };
      const submitted = this.interactionState.isWaitlistSubmitted(variant.id);
      const pending = !submitted && this.interactionState.isWatching(variant.id);
      const rowState = VaultifyWaitlistCore.resolveWaitlistRowState({ eligible, pending, submitted });
      return { eligible, pending, submitted, rowState, variant };
    }

    /** Every currently sold-out row that's WAITLIST-eligible AND not already submitted — the "select all" candidate pool, in _options order. */
    _eligibleRows() {
      if (!this.interactionState) return [];
      return this._options
        .filter((opt) => !opt.available)
        .map((opt) => ({ opt, info: this._rowWaitlistInfo(opt) }))
        .filter((r) => r.info.eligible && !r.info.submitted);
    }

    /**
     * Bell-icon row indicator, shared by every picker type whose rows
     * aren't rebuilt from scratch on every render as a full dropdown-style
     * option list AND every picker type that is (see
     * DropdownPicker._makeRow(), which also calls this) — `container` is
     * whatever element the badge should be appended to (a persistent
     * <label> for list/button, a freshly-built row element for dropdown).
     * Promoted here from ButtonPicker (originally written for it, Phase 3
     * continued) once list and dropdown also needed the identical
     * indicator instead of the dd-tag-wrap text tag — see the
     * architecture doc's "Row visual language" note. The dd-tag-*
     * CSS/vocabulary itself is left in place, unused by default, in case
     * a future per-picker configuration option reintroduces it as an
     * alternative rather than a replacement.
     *
     * Renders for all three eligible-row states, not just queued/
     * submitted — a not-yet-queued (ACTIONABLE) row now also gets a
     * badge, but styled neutral and hidden by default, revealed only on
     * hover/focus of the row (see the CSS is-notifiable:hover/:focus-
     * visible rules). This exists to restore the discoverability the
     * removed dd-tag-notify hover text used to provide ("what does
     * clicking this do?") without bringing back the text tag itself or
     * its layout-distorting/width-collision problems.
     *
     * Uses the same bell glyph already drawn in
     * docs/design/variants/pickers/button.html's own "persistent notify
     * bar" concept (.notify-bar-icon) — reused verbatim rather than a new
     * icon invented for this.
     */
    _syncBadge(container, rowState) {
      const ROW_STATE = VaultifyWaitlistCore.WAITLIST_ROW_STATE;
      if (!rowState) {
        container.querySelector('.wl-waitlist-badge')?.remove();
        return;
      }
      let badge = container.querySelector('.wl-waitlist-badge');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'wl-waitlist-badge';
        badge.setAttribute('aria-hidden', 'true');
        badge.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>';
        container.appendChild(badge);
      }
      badge.classList.toggle('wl-waitlist-badge--preview', rowState === ROW_STATE.ACTIONABLE);
      badge.classList.toggle('wl-waitlist-badge--queued', rowState === ROW_STATE.PENDING);
      badge.classList.toggle('wl-waitlist-badge--submitted', rowState === ROW_STATE.SUBMITTED);
    }

    // ── Waitlist: rendering ──────────────────────────────────────────────────

    /**
     * Optional per-subclass hook, called at the end of _renderWaitlistUI().
     * DropdownPicker uses it to update its compact trigger label; picker
     * types with no equivalent compact summary (e.g. ListPicker, whose rows
     * are always fully visible) simply don't implement it.
     */
    _updatePickerSummary() {}

    _renderWaitlistUI() {
      if (!this.interactionState || !this.waitlistEl) return;
      const ids = this.interactionState.getWaitlistVariantIds();
      const eligible = this._eligibleRows(); // already excludes submitted rows
      const notYetQueued = eligible.filter((r) => !r.info.pending);

      this._updatePickerSummary();

      const showContainer = ids.length > 0 || eligible.length > 0 || !!this._resultState;
      this.waitlistEl.hidden = !showContainer;

      // Explicitly set every sub-element on every call, rather than
      // returning early and leaving them untouched when showContainer is
      // false. The browser's default state for these elements (no "hidden"
      // attribute yet applied) is visible — a call that never reaches these
      // lines leaves whatever was last (or never) set, which surfaces as
      // Select all/Clear showing up before the waitlist provider has
      // signalled ready, since the very first render(s) legitimately have
      // no eligible rows yet.
      if (this.waitlistControlsEl)
        this.waitlistControlsEl.hidden = !showContainer || (eligible.length === 0 && ids.length === 0);
      if (this.selectAllBtn) this.selectAllBtn.hidden = !showContainer || notYetQueued.length === 0;
      if (this.clearWaitlistBtn) this.clearWaitlistBtn.hidden = !showContainer || ids.length === 0;
      // The "·" separator only makes sense between two visible buttons —
      // hide it whenever either side of it is hidden, not just when both are.
      if (this.waitlistSepEl) this.waitlistSepEl.hidden = this.selectAllBtn?.hidden || this.clearWaitlistBtn?.hidden;

      if (!showContainer) {
        if (this.waitlistPanelEl) this.waitlistPanelEl.innerHTML = '';
        return;
      }

      this._renderWaitlistPanel(ids);
    }

    // Progressive disclosure: collapsed by default (a compact "Get
    // notified" summary), expanding only on explicit request or once a
    // submission result needs to be shown. The dropdown remains the only
    // place variants are chosen — this panel consumes
    // ProductInteractionState's existing queue/submitted collections
    // read-only, it never recreates picker/selection logic of its own.
    _renderWaitlistPanel(ids) {
      if (!this.waitlistPanelEl) return;
      this.waitlistPanelEl.innerHTML = '';
      this._emailInputEl = null;
      this._emailErrorEl = null;
      this._submitBtnEl = null;

      const submittedIds = this.interactionState.getSubmittedWaitlistVariantIds();

      // A newly-queued item always resurfaces the card, even if a prior
      // completed batch was closed.
      if (ids.length) this._panelDismissed = false;

      // Closing a completed success banner (see _buildResultBanner) closes
      // the entire card, not just the message — nothing pending remains,
      // and the dropdown rows already carry the "Notified" confirmation on
      // their own, so there's nothing left here worth keeping on screen.
      if (!ids.length && this._panelDismissed && !this._resultState) return;

      if (!ids.length && !submittedIds.length && !this._resultState) return;

      // A result always needs the expanded view visible — collapsing right
      // after submitting would hide the very feedback the customer just
      // asked for.
      if (this._resultState) this._panelExpanded = true;

      if (!this._panelExpanded) {
        this.waitlistPanelEl.appendChild(this._buildCollapsedSummary(ids, submittedIds));
        return;
      }

      const panel = document.createElement('div');
      panel.className = 'dd-waitlist-form-wrap';
      // Focus target for the "Get notified" toggle — not otherwise
      // interactive itself, just a landing point when the panel expands.
      panel.setAttribute('tabindex', '-1');

      panel.appendChild(this._buildPanelHeader(ids));

      // Read-only queue summary, then email/submit — matches the approved
      // Concept G+F ordering. The "Waitlist" eyebrow that used to sit here
      // as its own line now lives in the header row instead (see
      // _buildPanelHeader()), so content starts one line higher.
      if (ids.length || submittedIds.length) panel.appendChild(this._buildQueueSummary(ids, submittedIds));

      // A failure/partial result still gets its own banner (with a retry
      // action) — only a success outcome moves into the header, see
      // _buildPanelHeader(). Persists even once ids is empty, until
      // dismissed or a new interaction starts (see _toggleWaitlistRow/
      // _selectAllSoldOut/_clearWaitlist/_pick, which all clear it first).
      if (this._resultState && this._resultState.outcome !== 'success') {
        panel.appendChild(this._buildResultBanner(this._resultState));
      }
      if (ids.length) panel.appendChild(this._buildSignupForm(ids));

      this.waitlistPanelEl.appendChild(panel);
    }

    /**
     * Compact default state: icon, count summary, and a toggle that
     * expands the panel. Never shows the queue itself, an email field, or
     * a submit button.
     *
     * ids (pending) can legitimately be empty while submittedIds isn't —
     * everything queued has already been successfully submitted, e.g. on
     * a later visit/reinit after a prior session's success. There's
     * nothing left to "get notified" about in that case, so the copy and
     * toggle switch to a confirmation reading ("N notifications set" /
     * "View") instead of literally describing zero pending items.
     */
    _buildCollapsedSummary(ids, submittedIds) {
      const allSubmitted = !ids.length && submittedIds.length > 0;

      const summary = document.createElement('div');
      summary.className = 'dd-waitlist-summary';

      const icon = document.createElement('span');
      icon.className = 'dd-waitlist-summary__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '🔔';
      summary.appendChild(icon);

      const text = document.createElement('div');
      text.className = 'dd-waitlist-summary__text';

      const title = document.createElement('p');
      title.className = 'dd-waitlist-summary__title';
      if (allSubmitted) {
        title.textContent =
          submittedIds.length === 1
            ? this._t('summaryTitleSubmittedOne', { option: this._optionLabelFor(submittedIds[0]) })
            : this._t('summaryTitleSubmittedMany', { count: submittedIds.length });
      } else {
        title.textContent =
          ids.length === 1
            ? this._t('summaryTitleOne', { option: this._optionLabelFor(ids[0]) })
            : this._t('summaryTitleMany', { count: ids.length, noun: this._pluralOptionNoun(ids.length) });
      }
      text.appendChild(title);

      const subtitle = document.createElement('p');
      subtitle.className = 'dd-waitlist-summary__subtitle';
      subtitle.textContent = this._t('summarySubtitle');
      text.appendChild(subtitle);

      summary.appendChild(text);

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'dd-waitlist-summary__toggle';
      toggle.setAttribute('aria-expanded', 'false');
      if (this.waitlistPanelEl?.id) toggle.setAttribute('aria-controls', this.waitlistPanelEl.id);
      const toggleLabel = document.createElement('span');
      toggleLabel.textContent = allSubmitted ? this._t('view', {}, 'View') : this._t('getNotified');
      toggle.appendChild(toggleLabel);
      const chevron = document.createElement('span');
      chevron.className = 'dd-waitlist-summary__chevron';
      chevron.setAttribute('aria-hidden', 'true');
      chevron.textContent = '▾';
      toggle.appendChild(chevron);
      toggle.addEventListener('click', () => {
        this._panelExpanded = true;
        this._renderWaitlistUI();
        // Move focus into the expanded panel so keyboard/AT users land
        // somewhere meaningful rather than staying on a now-removed button.
        this.waitlistPanelEl?.querySelector('.dd-waitlist-form-wrap')?.focus();
      });
      summary.appendChild(toggle);

      return summary;
    }

    /**
     * Read-only reflection of the existing queue/submitted collections —
     * with one exception: a pending entry gets a small remove control,
     * calling the same public removeWaitlistVariant(variantId) the
     * dropdown row/Clear already use. Submitted entries never get one —
     * there is no "undo a completed signup" concept, on this control or
     * anywhere else.
     *
     * One flat list, not two headed groups ("Watching"/"Notification
     * set") — a second uppercase label competing with the component's own
     * "Waitlist" title was exactly the fragmented-hierarchy problem this
     * pass set out to fix. A submitted entry is distinguished inline (a
     * quiet trailing "Notified" instead of its own heading), not by
     * introducing another emphasis level.
     */
    _buildQueueSummary(pendingIds, submittedIds) {
      const list = document.createElement('ul');
      list.className = 'dd-waitlist-queue-summary__list';

      pendingIds.forEach((id) => {
        const item = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = '✓ ' + this._optionLabelFor(id);
        item.appendChild(label);

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'dd-waitlist-queue-summary__remove';
        removeBtn.setAttribute('aria-label', this._t('removeFromWaitlist', { option: this._optionLabelFor(id) }));
        removeBtn.textContent = '×';
        removeBtn.addEventListener('click', () => {
          this.interactionState.removeWaitlistVariant(id);
          this._resultState = null;
          this._render(); // the row for this variant reverts to actionable
          this._renderWaitlistUI();
          // The clicked button is destroyed by the re-render above — land
          // focus back on the panel rather than losing it to document body.
          this.waitlistPanelEl?.querySelector('.dd-waitlist-form-wrap')?.focus();
        });
        item.appendChild(removeBtn);

        list.appendChild(item);
      });

      submittedIds.forEach((id) => {
        const item = document.createElement('li');
        item.className = 'dd-waitlist-queue-summary__list-item--submitted';
        item.appendChild(document.createTextNode('✓ ' + this._optionLabelFor(id) + ' '));
        const meta = document.createElement('span');
        meta.className = 'dd-waitlist-queue-summary__meta';
        meta.textContent = this._t('notifiedSuffix', {}, 'Notified');
        item.appendChild(meta);
        list.appendChild(item);
      });

      return list;
    }

    /**
     * The panel's top row — always has a toggle button on the right, plus
     * content sitting to its left in place of a bare caret: the "Waitlist"
     * eyebrow label in the normal (pending-queue) state, or the success
     * copy once a submission has succeeded. Consolidating these was a
     * deliberate simplification — previously the eyebrow was its own line
     * below the header, and a success result rendered as a fully separate
     * bordered/tinted banner further down the card (see
     * _buildResultBanner(), now only used for failure/partial) with the
     * collapse caret sitting alone above both — three separate elements
     * doing what fits in one row. Failure/partial results keep their own
     * banner (a retry action, and a failure count, aren't things this
     * header has room for), and the header falls back to the plain eyebrow
     * + caret in that case, same as the normal pending state.
     *
     * @param {string[]} ids - current pending queue IDs, only used to
     *   decide whether the eyebrow renders (matches _buildSignupForm's own
     *   gate — no eyebrow for a submitted-only panel, same as before).
     */
    _buildPanelHeader(ids) {
      const header = document.createElement('div');
      header.className = 'dd-waitlist-form-wrap__header';

      const isSuccess = this._resultState?.outcome === 'success';

      if (isSuccess) {
        const msg = document.createElement('p');
        msg.className = 'dd-waitlist-form-wrap__header-message';
        msg.setAttribute('role', 'status');
        // Focus target for _handleSubmit()'s post-submission focus move —
        // see the combined selector there (.dd-waitlist-result,
        // .dd-waitlist-form-wrap__header-message).
        msg.setAttribute('tabindex', '-1');
        msg.textContent = this._resultState.message;
        header.appendChild(msg);
      } else if (ids.length) {
        const heading = document.createElement('p');
        heading.className = 'dd-waitlist-form__heading';
        heading.textContent = this._t('waitlistHeading', {}, 'Waitlist');
        header.appendChild(heading);
      }

      const toggleBtn = document.createElement('button');
      toggleBtn.type = 'button';
      toggleBtn.className = 'dd-waitlist-form-wrap__collapse';
      if (this.waitlistPanelEl?.id) toggleBtn.setAttribute('aria-controls', this.waitlistPanelEl.id);

      if (isSuccess) {
        // Closes the whole card, not just this message — see
        // _panelDismissed and the guard in _renderWaitlistPanel. Once
        // nothing is pending, the rows already carry their own "Notified"
        // confirmation, so there's nothing left here to keep open after
        // the customer has read it.
        toggleBtn.setAttribute('aria-label', this._t('close', {}, 'Close'));
        toggleBtn.textContent = '×';
        toggleBtn.addEventListener('click', () => {
          this._resultState = null;
          this._panelDismissed = true;
          this._renderWaitlistUI();
        });
      } else {
        toggleBtn.setAttribute('aria-expanded', 'true');
        toggleBtn.setAttribute('aria-label', this._t('collapse', {}, 'Collapse'));
        toggleBtn.textContent = '▴';
        toggleBtn.addEventListener('click', () => {
          this._panelExpanded = false;
          this._renderWaitlistUI();
        });
      }
      header.appendChild(toggleBtn);

      return header;
    }

    /** Failure/partial results only — a success result moves into _buildPanelHeader() instead. */
    _buildResultBanner(resultState) {
      const banner = document.createElement('div');
      banner.className = 'dd-waitlist-result dd-waitlist-result--' + resultState.outcome;
      banner.setAttribute('role', 'status');
      banner.setAttribute('tabindex', '-1');

      const msg = document.createElement('p');
      msg.className = 'dd-waitlist-result__message';
      msg.textContent = resultState.message;
      banner.appendChild(msg);

      const retryBtn = document.createElement('button');
      retryBtn.type = 'button';
      retryBtn.className = 'dd-waitlist-result__retry';
      retryBtn.textContent = this._t('retry');
      retryBtn.addEventListener('click', () => {
        this._resultState = null;
        this._renderWaitlistUI();
      });
      banner.appendChild(retryBtn);

      return banner;
    }

    _buildSignupForm(ids) {
      const form = document.createElement('div');
      form.className = 'dd-waitlist-form';

      // Logged-in customers see their account email as fixed, unclickable
      // text — no "use a different email" override in this phase (see the
      // architecture doc: the alternate-email/identity semantics against
      // STOQ's API aren't confirmed, so the simplest safe behaviour is used
      // rather than inventing provider behaviour).
      const customer = this.interactionState.getCustomerContext();
      if (customer?.email) {
        const notice = document.createElement('p');
        notice.className = 'dd-waitlist-form__account-email';
        notice.textContent = this._t('accountEmailNotice', { email: customer.email });
        form.appendChild(notice);
      } else {
        const field = document.createElement('div');
        field.className = 'dd-waitlist-form__field';

        const emailInput = document.createElement('input');
        emailInput.type = 'email';
        emailInput.className = 'dd-waitlist-form__email';
        emailInput.placeholder = this._t('emailPlaceholder');
        emailInput.setAttribute('autocomplete', 'email');
        emailInput.setAttribute('inputmode', 'email');
        emailInput.setAttribute('aria-label', this._t('emailLabel'));
        if (this._pendingEmail) emailInput.value = this._pendingEmail;
        field.appendChild(emailInput);
        form.appendChild(field);
        this._emailInputEl = emailInput;

        const errorEl = document.createElement('p');
        errorEl.className = 'dd-waitlist-form__error';
        errorEl.hidden = true;
        form.appendChild(errorEl);
        this._emailErrorEl = errorEl;
      }

      const submitBtn = document.createElement('button');
      submitBtn.type = 'button';
      submitBtn.className = 'dd-waitlist-form__submit';
      submitBtn.textContent =
        ids.length === 1
          ? this._t('submit')
          : this._t('submitMany', { count: ids.length, noun: this._pluralOptionNoun(ids.length) });
      submitBtn.disabled = this._submitting;
      submitBtn.addEventListener('click', () => this._handleSubmit());
      form.appendChild(submitBtn);
      this._submitBtnEl = submitBtn;

      const consent = document.createElement('p');
      consent.className = 'dd-waitlist-form__consent';
      consent.textContent = this._t('consentNote');
      form.appendChild(consent);

      return form;
    }

    async _handleSubmit() {
      if (this._submitting || !this.interactionState) return;

      let email;
      if (this._emailInputEl) {
        email = this._emailInputEl.value.trim();
        if (!this._isValidEmail(email)) {
          if (this._emailErrorEl) {
            this._emailErrorEl.textContent = this._t('invalidEmail');
            this._emailErrorEl.hidden = false;
          }
          this._emailInputEl.setAttribute('aria-invalid', 'true');
          this._emailInputEl.focus();
          return;
        }
        this._pendingEmail = email;
      }

      this._submitting = true;
      if (this._submitBtnEl) {
        this._submitBtnEl.disabled = true;
        this._submitBtnEl.textContent = this._t('submitting');
      }
      this._announce(this._t('submitting'));

      const idsBefore = this.interactionState.getWaitlistVariantIds();
      const result = await this.interactionState.submitWaitlist(email ? { email } : {});
      this._submitting = false;

      // 'invalid-payload'/'unknown-variant' are pure theme-side bugs (a
      // malformed request never reached the network at all) — distinct
      // from 'network-error'/'http-error'/'provider-error', which
      // originate from STOQ's own response and stay silent by design to
      // avoid ever logging customer data. Structural failures carry no
      // customer data (providerErrors here are JS error messages about
      // missing fields, not anything from STOQ), so surfacing them costs
      // nothing and saves a repeat of the manual console debugging it took
      // to find this exact bug once already.
      result.failed
        .filter((f) => f.reason === 'invalid-payload' || f.reason === 'unknown-variant')
        .forEach((f) => console.error('[vaultify-waitlist] submission failed before reaching the network:', f));

      const outcome = VaultifyWaitlistCore.classifySubmissionOutcome(result);
      if (outcome === 'success') this._pendingEmail = null;
      this._resultState = { outcome, result, message: this._resultMessage(outcome, result, idsBefore) };

      this._render();
      this._renderWaitlistUI();
      this.waitlistPanelEl?.querySelector('.dd-waitlist-result, .dd-waitlist-form-wrap__header-message')?.focus();
      this._announce(this._resultState.message);
    }

    _resultMessage(outcome, result, idsBefore) {
      if (outcome === 'success') {
        return idsBefore.length === 1
          ? this._t('successOne', { option: this._optionLabelFor(idsBefore[0]) })
          : this._t('successMany', { count: idsBefore.length, noun: this._pluralOptionNoun(idsBefore.length) });
      }
      if (outcome === 'partial') {
        return this._t('partialSuccess', { succeeded: result.succeeded.length, failed: result.failed.length });
      }
      return this._t('error');
    }

    /**
     * Option label for a resolved variant ID. Looks the variant up
     * directly via ProductInteractionState.getVariantById() — reading its
     * own stored title (Shopify's full option-path string, e.g.
     * "With Retail Box / 25 Toploaders") — rather than searching
     * this._options for a matching currently-rendered row.
     *
     * That search-based approach broke once a customer could queue items
     * across multiple branches (Phase X: journey exploration, but the same
     * gap exists on any multi-axis product): this._options only ever
     * contains whatever rows are CURRENTLY visible for the CURRENT other-
     * axis selection, so a queued variant belonging to a branch no longer
     * in view isn't found among them at all — confirmed live, showing a
     * blank label for two of three queued items after exploring a second
     * Packaging branch. getVariantById() reads the variant directly from
     * the trusted array, independent of what's currently rendered, so it
     * works regardless of which branch happens to be in view.
     *
     * Also still works right after the ID has been removed from the queue
     * post-submit — variant.title is a static property of the resolved
     * variant, not derived from live queue/row state either way.
     */
    _optionLabelFor(variantId) {
      if (!this.interactionState) return '';
      return this.interactionState.getVariantById(variantId)?.title || '';
    }

    _isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }
  }

  // Exposed so a component that needs the same waitlist panel without being a variant picker (the series strip,
  // assets/vaultify-series.js) extends it instead of copying it.
  VaultifyPickers.BasePicker = BasePicker;

  // ── Dropdown picker ────────────────────────────────────────────────────────

  class DropdownPicker extends BasePicker {
    static selector = '.dd-wrap';

    _init() {
      this.trigger = this.wrap.querySelector('.dd-trigger');
      this.triggerLbl = this.wrap.querySelector('.dd-trigger-label');
      this.listEl = this.wrap.querySelector('.dd-list');
      this.searchEl = this.wrap.querySelector('.dd-search');
      this.clearBtn = this.wrap.querySelector('.dd-clear');

      this.isOpen = false;
      this.query = '';
      this.focusedIdx = -1;
      this._flat = [];

      // ── Phase 2: waitlist (Concept G+F) ──────────────────────────────────
      // interactionState is Phase 1's ProductInteractionState — the sole
      // authority for resolution/Purchase State/queue. This picker never
      // resolves variants, computes Purchase State, or touches the queue's
      // backing store itself; it only calls the public API and renders.
      this.interactionState = VaultifyPickers.getInteractionState(this.wrap);
      this.optionName = this.wrap.dataset.optionName || 'option';
      const container = this.wrap.parentElement; // .product-form__input--dropdown-custom
      this.waitlistEl = container?.querySelector('[data-dd-waitlist]');
      this.waitlistControlsEl = this.waitlistEl?.querySelector('[data-dd-waitlist-controls]');
      this.selectAllBtn = this.waitlistEl?.querySelector('[data-dd-waitlist-select-all]');
      this.clearWaitlistBtn = this.waitlistEl?.querySelector('[data-dd-waitlist-clear]');
      this.waitlistSepEl = this.waitlistEl?.querySelector('[data-dd-waitlist-sep]');
      this.waitlistPanelEl = this.waitlistEl?.querySelector('[data-dd-waitlist-panel]');
      // Stable id for aria-controls from the "Get notified" toggle — the
      // panel has no id in the server-rendered markup, and this only needs
      // to be unique within the current DOM, not persisted anywhere.
      if (this.waitlistPanelEl && !this.waitlistPanelEl.id) {
        this.waitlistPanelEl.id = 'dd-waitlist-panel-' + Math.random().toString(36).slice(2, 9);
      }
      this.statusEl = container?.querySelector('[data-dd-waitlist-status]');
      this._submitting = false;
      this._resultState = null; // { outcome: 'success'|'partial'|'failure', result, message }
      this._pendingEmail = null; // guest email, preserved across a failed/partial retry
      // Progressive disclosure: the panel starts collapsed to a compact
      // summary ("Get notified") and only shows the queue summary/email/
      // submit form once the customer explicitly expands it (or a
      // submission result needs to be shown). Deliberately not reset by
      // queue changes — expanding/collapsing again mid-selection would be
      // more surprising than leaving it as the customer last set it.
      this._panelExpanded = false;
      // Set when the customer explicitly closes a completed (all-submitted,
      // nothing pending) success banner. Once every queued item already
      // shows its own "Notified" pill on the dropdown row, a lingering
      // summary card repeats that same confirmation with no new
      // information and nothing left to act on — closing it should remove
      // the whole card, not shrink it to a smaller card saying the same
      // thing. Cleared automatically the moment a new item is queued
      // (see _renderWaitlistPanel), so a future waitlist request always
      // resurfaces normally.
      this._panelDismissed = false;

      this._buildOptions();
      this._populatePrices();
      this._attachListeners();
      this._attachWaitlistListeners();
      // Pre-bound the panel on init so it never extends beyond an overflow
      // ancestor (e.g. .quick-add-modal__content-info) before first open.
      this._positionPanel();
      // Reflect whatever the queue already contains (survives reinit — see
      // ProductInteractionState) the moment this instance is (re)constructed.
      this._renderWaitlistUI();
      // Re-run this idempotent, pure-function-of-current-state render on
      // "product-info:loaded" — the same settling signal SyncVariantVisibility
      // already relies on — so eligible rows are re-checked after any
      // variant-driven section refresh, not just at construction.
      this._onWaitlistInfoLoaded = () => this._renderWaitlistUI();
      document.addEventListener('product-info:loaded', this._onWaitlistInfoLoaded);
      // isWaitlistCapable() polls the STOQ provider's isReady() live, with
      // no ready/change callback of its own — the provider can genuinely
      // still be loading at construction time (its own script tag hasn't
      // finished initialising window._RestockRocket yet) and become ready
      // moments later, with nothing else guaranteed to trigger a re-render
      // in between. Waiting for the customer to open the dropdown before
      // this area updates is bad UX — they have no cue the waitlist option
      // is coming and may never open it at all. Poll for readiness directly
      // for a short, bounded window after construction instead: cheap
      // (idempotent render, ~200ms cadence, stops the moment it's ready or
      // after a generous ceiling), and means the controls appear on their
      // own once the provider loads, not only on interaction.
      let waitlistReadyPolls = 0;
      const maxWaitlistReadyPolls = 40; // ~8s at 200ms — comfortably past STOQ's typical init time
      this._waitlistReadyPoll = setInterval(() => {
        waitlistReadyPolls++;
        this._renderWaitlistUI();
        const ready = !!VaultifyPickers.getWaitlistProvider()?.isReady();
        if (ready || waitlistReadyPolls >= maxWaitlistReadyPolls) {
          clearInterval(this._waitlistReadyPoll);
          this._waitlistReadyPoll = null;
        }
      }, 200);
    }

    _destroy() {
      this.close();
      document.removeEventListener('click', this._onOutside);
      if (this._waitlistReadyPoll) clearInterval(this._waitlistReadyPoll);
      document.removeEventListener('product-info:loaded', this._onWaitlistInfoLoaded);
    }

    // ── Dropdown-specific rendering ──────────────────────────────────────────
    // _t/_pluralOptionNoun/_announce and everything under "Waitlist panel"
    // are inherited from BasePicker — shared with ListPicker, not duplicated.

    _buildOptions() {
      this._options = Array.from(this.listEl.querySelectorAll('.dd-option')).map((el) => ({
        value: el.dataset.value,
        available: !el.classList.contains('is-sold-out'),
        selected: el.classList.contains('is-selected'),
        price: '',
      }));
      const preSelected = this._options.find((o) => o.selected);
      if (preSelected && !preSelected.available) this.wrap.classList.add('has-sold-out');
    }

    _populatePrices() {
      this._options.forEach((opt) => {
        const cents = this._getPriceCents(opt.value);
        if (cents === null) return;
        opt.price = this._formatPrice(cents);
        const row = Array.from(this.listEl.querySelectorAll('.dd-option')).find((el) => el.dataset.value === opt.value);
        if (row) {
          const priceEl = row.querySelector('[data-dd-price]');
          if (priceEl) priceEl.textContent = opt.price;
        }
      });
    }

    _attachListeners() {
      this.trigger.addEventListener('click', (e) => {
        if (e.target.closest('.dd-clear')) return;
        this.isOpen ? this.close() : this.open();
      });

      this.clearBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._clearSelection();
      });

      this.searchEl?.addEventListener('input', () => {
        this.query = this.searchEl.value.toLowerCase();
        this.focusedIdx = -1;
        this._render();
      });

      this.wrap.addEventListener('keydown', (e) => this._keydown(e));
      this._onOutside = (e) => {
        // Row clicks that toggle a waitlist entry call _render(), which
        // rebuilds the row list (listEl.innerHTML = '') synchronously —
        // including replacing e.target itself — before this listener runs
        // on the same click's bubble phase. By then wrap.contains(e.target)
        // is false (the clicked node has been detached), so a plain
        // contains() check would incorrectly treat the click as "outside"
        // and close the dropdown mid multi-select. composedPath() captures
        // the event's path at dispatch time and stays valid even after the
        // target node is removed, so use it first.
        const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
        if (path.includes(this.wrap) || this.wrap.contains(e.target)) return;
        this.close();
      };
    }

    _attachWaitlistListeners() {
      this.selectAllBtn?.addEventListener('click', () => this._selectAllSoldOut());
      this.clearWaitlistBtn?.addEventListener('click', () => this._clearWaitlist());
    }

    // ── Open / close ─────────────────────────────────────────────────────────

    open() {
      this.isOpen = true;
      this.query = '';
      this.focusedIdx = -1;
      if (this.searchEl) this.searchEl.value = '';
      this._render();
      // _render() re-checks each row's own eligibility live, but the Select
      // all/Clear controls are a separate container driven entirely by
      // _renderWaitlistUI(), which nothing else guarantees has run recently
      // — isWaitlistCapable() polls the provider's readiness live, with no
      // ready callback, so a page-load-time render can be stale by the time
      // the customer actually opens the dropdown. This is the moment they
      // look at this area, so re-check it here rather than relying on
      // whichever background signal happened to fire last.
      this._renderWaitlistUI();
      // Decide open direction before adding is-open so the CSS transition
      // starts in the right direction.
      this._positionPanel();
      this.wrap.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      // _flat is now built in rendered order (available first, then sold-out).
      // Resolve focusedIdx against _flat so _applyFocus targets the right row.
      this.focusedIdx = this._flat.findIndex((o) => o.selected);
      this._applyFocus();
      if (this.searchEl && this._options.length > 6) {
        requestAnimationFrame(() => this.searchEl.focus());
      }
      document.addEventListener('click', this._onOutside);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.wrap.classList.remove('is-open', 'opens-up');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this._onOutside);
      // Keep the bounded maxHeight rather than clearing it — clearing would
      // restore the CSS default (280px) which can extend beyond an overflow
      // ancestor and trigger an unexpected scrollbar in modal contexts.
    }

    // Returns the top and bottom of the nearest overflow-clipping ancestor —
    // the actual boundary the panel will be clipped against. Falls back to
    // the viewport edges so the logic also works on the full product page.
    _getOverflowBoundary() {
      let el = this.wrap.parentElement;
      while (el && el !== document.body) {
        const { overflowY, overflow } = window.getComputedStyle(el);
        if (overflowY === 'auto' || overflowY === 'scroll' || overflow === 'auto' || overflow === 'scroll') {
          const r = el.getBoundingClientRect();
          return { top: r.top, bottom: r.bottom };
        }
        el = el.parentElement;
      }
      return { top: 0, bottom: window.innerHeight };
    }

    // Choose open direction and cap panel max-height to the available space
    // within the overflow-clipping ancestor so the panel is never clipped.
    // Direction: whichever side has more room. Max-height: available space
    // minus 8px breathing room so the shadow isn't flush against the edge.
    _positionPanel() {
      const triggerRect = this.trigger.getBoundingClientRect();
      const panel = this.wrap.querySelector('.dd-panel');
      if (!panel) return;
      const boundary = this._getOverflowBoundary();
      const spaceBelow = boundary.bottom - triggerRect.bottom;
      const spaceAbove = triggerRect.top - boundary.top;
      const opensUp = spaceAbove > spaceBelow;
      this.wrap.classList.toggle('opens-up', opensUp);
      const available = (opensUp ? spaceAbove : spaceBelow) - 8;
      panel.style.maxHeight = Math.max(80, available) + 'px';
    }

    // ── Selection ────────────────────────────────────────────────────────────

    _pick(value) {
      this._options.forEach((o) => {
        o.selected = o.value === value;
      });
      this.triggerLbl.textContent = value;
      this.wrap.classList.add('has-selection');
      const picked = this._options.find((o) => o.value === value);
      this.wrap.classList.toggle('has-sold-out', !!(picked && !picked.available));
      this.close();
      this._dispatchChange(value);
      // Deliberately does not touch the waitlist queue — a customer
      // comparing options by picking an in-stock variant shouldn't lose
      // waitlist selections they've already made on other, sold-out
      // variants. The trigger label above reflects this purchase
      // selection; _updatePickerSummary() gives it priority over the
      // waitlist summary whenever both exist, so there's no ambiguity
      // about what Add to Cart will act on.
      //
      // The submission result banner (success/partial/failure), by
      // contrast, is stale UI state once the customer has moved on to
      // picking something else to view/buy — clear it, independently of
      // the queue itself.
      if (this._resultState) {
        this._resultState = null;
        this._renderWaitlistUI();
      }
    }

    _clearSelection() {
      this._options.forEach((o) => {
        o.selected = false;
      });
      this.wrap.classList.remove('has-selection', 'has-sold-out');
      this.triggerLbl.textContent = this.wrap.dataset.placeholder || '';
    }

    // ── Render ───────────────────────────────────────────────────────────────

    _filtered() {
      if (!this.query) return this._options;
      return this._options.filter((o) => o.value.toLowerCase().includes(this.query));
    }

    _render() {
      const all = this._filtered();
      const avail = all.filter((o) => o.available);
      const sold = all.filter((o) => !o.available);

      this.listEl.innerHTML = '';
      this._flat = [];

      if (!all.length) {
        const empty = document.createElement('div');
        empty.className = 'dd-empty';
        empty.textContent = `No options match "${this.query}"`;
        this.listEl.appendChild(empty);
        return;
      }

      avail.forEach((opt) => {
        this.listEl.appendChild(this._makeRow(opt));
        this._flat.push(opt);
      });

      if (sold.length) {
        const sep = document.createElement('div');
        sep.className = 'dd-sep';
        sep.setAttribute('role', 'separator');
        sep.innerHTML =
          '<span class="dd-sep-line"></span>' +
          '<span class="dd-sep-label">Sold out</span>' +
          '<span class="dd-sep-line"></span>';
        this.listEl.appendChild(sep);
        sold.forEach((opt) => {
          this.listEl.appendChild(this._makeRow(opt));
          this._flat.push(opt);
        });
      }

      this._applyFocus();
    }

    // _rowWaitlistInfo/_eligibleRows are inherited from BasePicker — shared
    // with ListPicker, not duplicated.

    /**
     * True when this dropdown's step (if any) is the LAST step in its
     * journey, or when it isn't inside a journey step at all — mirrors
     * SyncVariantVisibility._isFinalStep() (same concept, different
     * starting element: this.wrap vs. a fieldset). Only a sold-out row on
     * an EARLIER step can be a branch to explore (Phase X); a sold-out row
     * on the final step is a genuine waitlist ROW, owned by the existing
     * is-notifiable machinery above.
     */
    _isFinalJourneyStep() {
      const step = this.wrap.closest('.variant-step');
      if (!step) return true;
      const steps = Array.from(this.wrap.closest('variant-selects')?.querySelectorAll('.variant-step') || []);
      return steps.length > 0 && steps[steps.length - 1] === step;
    }

    _makeRow(opt) {
      const row = document.createElement('div');
      row.className = 'dd-option';
      row.setAttribute('role', 'option');
      row.setAttribute('aria-selected', String(opt.selected));
      if (!opt.available) row.classList.add('is-sold-out');
      if (opt.selected) row.classList.add('is-selected');

      const waitlistInfo = this._rowWaitlistInfo(opt);
      const ROW_STATE = VaultifyWaitlistCore.WAITLIST_ROW_STATE;
      if (waitlistInfo.rowState) row.classList.add('is-notifiable');
      if (waitlistInfo.rowState === ROW_STATE.PENDING) row.classList.add('is-queued');
      if (waitlistInfo.rowState === ROW_STATE.SUBMITTED) row.classList.add('is-submitted');

      // Journey branch exploration (Phase X): an earlier step's sold-out
      // row with no final-step waitlist state of its own (rowState above
      // is null — resolveCandidate can't fully resolve to one variant
      // while later axes are still unchosen) may still lead to a
      // WAITLIST-eligible descendant. getBranchState() is the same
      // Purchase State authority every other eligibility check here uses.
      let isBranchExplorable = false;
      if (!opt.available && !waitlistInfo.rowState && this.interactionState && !this._isFinalJourneyStep()) {
        const branchState = this.interactionState.getBranchState(this.optionIdx, opt.value);
        isBranchExplorable = branchState === VaultifyWaitlistCore.BRANCH_STATE.WAITLIST_EXPLORABLE;
      }
      if (isBranchExplorable) {
        row.classList.add('is-branch-explorable');
        row.setAttribute('data-branch-explorable', '');
        row.setAttribute('data-branch-value', opt.value);
      }

      const nameEl = document.createElement('span');
      nameEl.className = 'dd-opt-name';
      if (this.query) nameEl.appendChild(this._highlight(opt.value));
      else nameEl.textContent = opt.value;

      const right = document.createElement('span');
      right.className = 'dd-opt-right';

      if (opt.price) {
        const priceEl = document.createElement('span');
        priceEl.className = 'dd-opt-price';
        priceEl.textContent = opt.price;
        right.appendChild(priceEl);
      }

      // Queued/submitted rows get the shared checkmark badge (BasePicker's
      // _syncBadge, promoted from ButtonPicker once this class also needed
      // it — see its definition). A not-yet-queued eligible row gets no
      // badge at all, same as every other picker type now — the
      // is-notifiable row styling (cursor, background) is its only
      // affordance; a plain sold-out row with nothing to do (unresolved,
      // invalid, preorder, or no provider) shows just name + price, same
      // as before.
      this._syncBadge(right, waitlistInfo.rowState);

      row.appendChild(nameEl);
      row.appendChild(right);

      if (!opt.available) {
        const key = isBranchExplorable
          ? 'branchExplorable'
          : waitlistInfo.rowState === ROW_STATE.SUBMITTED
            ? 'rowStateSubmitted'
            : waitlistInfo.rowState === ROW_STATE.PENDING
              ? 'rowStateWatching'
              : waitlistInfo.rowState === ROW_STATE.ACTIONABLE
                ? 'rowStateNotifiable'
                : 'rowStateSoldOut';
        row.setAttribute('aria-label', this._t(key, { option: opt.value }));
      }

      // A submitted row is never toggleable back into the active queue —
      // only actionable/pending rows respond to click (toggle in/out).
      // Branch-explorable rows attach no click listener of their own —
      // the click bubbles to product-info root, where JourneyOrchestrator's
      // delegated [data-branch-explorable] handler owns advancing the
      // journey (see _handleClick). This picker never decides whether a
      // branch is explorable, only renders the state.
      if (opt.available) {
        row.addEventListener('click', () => this._pick(opt.value));
      } else if (waitlistInfo.rowState === ROW_STATE.ACTIONABLE || waitlistInfo.rowState === ROW_STATE.PENDING) {
        row.addEventListener('click', () => this._toggleWaitlistRow(opt));
      }

      return row;
    }

    _highlight(text) {
      const frag = document.createDocumentFragment();
      const q = this.query;
      const idx = text.toLowerCase().indexOf(q);
      if (idx === -1) {
        frag.appendChild(document.createTextNode(text));
        return frag;
      }
      if (idx > 0) frag.appendChild(document.createTextNode(text.slice(0, idx)));
      const mark = document.createElement('mark');
      mark.className = 'dd-hl';
      mark.textContent = text.slice(idx, idx + q.length);
      frag.appendChild(mark);
      const tail = text.slice(idx + q.length);
      if (tail) frag.appendChild(document.createTextNode(tail));
      return frag;
    }

    _applyFocus() {
      const rows = this.listEl.querySelectorAll('.dd-option');
      rows.forEach((el, i) => {
        const focused = i === this.focusedIdx;
        el.classList.toggle('is-focused', focused);
        if (focused) el.scrollIntoView({ block: 'nearest' });
      });
    }

    _keydown(e) {
      if (!this.isOpen) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          this.open();
        }
        return;
      }
      const n = this.listEl.querySelectorAll('.dd-option').length;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.focusedIdx = Math.min(this.focusedIdx + 1, n - 1);
          this._applyFocus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.focusedIdx = Math.max(this.focusedIdx - 1, 0);
          this._applyFocus();
          break;
        case 'Enter':
          e.preventDefault();
          if (this.focusedIdx >= 0 && this.focusedIdx < this._flat.length) {
            const opt = this._flat[this.focusedIdx];
            if (opt.available) {
              this._pick(opt.value);
            } else {
              const rowState = this._rowWaitlistInfo(opt).rowState;
              const ROW_STATE = VaultifyWaitlistCore.WAITLIST_ROW_STATE;
              if (rowState === ROW_STATE.ACTIONABLE || rowState === ROW_STATE.PENDING) {
                this._toggleWaitlistRow(opt);
              } else {
                // Branch-explorable row: no dedicated method here by design
                // (see _makeRow) — .click() bubbles to product-info root,
                // where JourneyOrchestrator's delegated handler owns it.
                const rowEl = this.listEl.querySelectorAll('.dd-option')[this.focusedIdx];
                if (rowEl?.hasAttribute('data-branch-explorable')) rowEl.click();
              }
            }
          }
          break;
        case 'Escape':
          this.close();
          this.trigger.focus();
          break;
        case 'Tab':
          this.close();
          break;
      }
    }

    // ── Waitlist: queue interaction ──────────────────────────────────────────
    // Every method here does exactly one thing: call the ProductInteractionState
    // API, then re-render. None of them resolve variants, compute Purchase
    // State, or touch a queue backing store — that's Phase 1's job.

    /** Row click/Enter on an eligible sold-out row. Does not close the dropdown — multi-select stays fluid across several toggles. */
    _toggleWaitlistRow(opt) {
      if (!this.interactionState) return;
      this.interactionState.toggleWaitlistVariant(this.optionIdx, opt.value);
      this._resultState = null;
      // _render() rebuilds every row from scratch and re-applies focus from
      // this.focusedIdx — without updating it first, a mouse click (which
      // never touches focusedIdx the way arrow-key navigation does) leaves
      // it pointing at whatever row was focused when the panel opened,
      // visually yanking focus/scroll back to that row instead of staying
      // on the one just toggled. _flat's order is unaffected by a waitlist
      // toggle (only sold-out/available partitioning changes row order, and
      // toggling doesn't change that), so resolving the index beforehand
      // still lines up with the rebuilt list.
      const idx = this._flat.indexOf(opt);
      if (idx !== -1) this.focusedIdx = idx;
      this._render(); // refresh row visuals (queued state) in place
      this._renderWaitlistUI();
      const info = this._rowWaitlistInfo(opt);
      this._announce(this._t(info.pending ? 'rowStateWatching' : 'rowStateNotifiable', { option: opt.value }));
    }

    /** Adds every currently-eligible sold-out row — the full _options set, not just what search filtering currently shows. */
    _selectAllSoldOut() {
      if (!this.interactionState) return;
      this._eligibleRows().forEach(({ opt }) => this.interactionState.addWaitlistVariant(this.optionIdx, opt.value));
      this._resultState = null;
      this._render();
      this._renderWaitlistUI();
      this._announce(
        this._t('triggerMany', {
          count: this.interactionState.getWaitlistVariantIds().length,
          noun: this._pluralOptionNoun(this.interactionState.getWaitlistVariantIds().length),
        }),
      );
    }

    _clearWaitlist() {
      if (!this.interactionState) return;
      this.interactionState.clearWaitlist();
      this._resultState = null;
      this._pendingEmail = null;
      this._render();
      this._renderWaitlistUI();
    }

    // ── Waitlist: rendering ──────────────────────────────────────────────────

    /** Overrides BasePicker's no-op hook, called at the end of _renderWaitlistUI(). */
    _updatePickerSummary() {
      if (!this.interactionState) return;
      const ids = this.interactionState.getWaitlistVariantIds();
      // A genuine purchase selection (from _options, the source of truth
      // _pick()/_clearSelection() maintain) always wins the trigger label,
      // regardless of what's in the waitlist queue — picking an available
      // variant no longer clears the queue (see _pick()), so both can be
      // true at once. The trigger reflects what Add to Cart will act on;
      // the waitlist queue's own summary lives in the Select all/Clear row
      // below, not the trigger.
      const selectedOpt = this._options.find((o) => o.selected);

      if (selectedOpt) {
        this.wrap.classList.remove('has-sold-out');
        this.wrap.classList.add('has-selection');
        this.wrap.classList.toggle('has-sold-out', !selectedOpt.available);
        this.triggerLbl.textContent = selectedOpt.value;
        return;
      }

      if (!ids.length) {
        this.wrap.classList.remove('has-selection', 'has-sold-out');
        this.triggerLbl.textContent = this.wrap.dataset.placeholder || '';
        return;
      }

      this.wrap.classList.remove('has-selection');
      this.wrap.classList.add('has-sold-out');
      if (ids.length === 1) {
        this.triggerLbl.textContent = this._t('triggerOne', { option: this._optionLabelFor(ids[0]) });
        this.triggerLbl.removeAttribute('title');
      } else {
        this.triggerLbl.textContent = this._t('triggerMany', {
          count: ids.length,
          noun: this._pluralOptionNoun(ids.length),
        });
        // Native tooltip on hover — the compact count stays compact, but a
        // mouse user can still see exactly which variants it refers to
        // without opening the panel.
        this.triggerLbl.title = this._queuedLabelsList(ids);
      }
    }

    /** Comma-joined list of option labels for a set of queued variant IDs, in queue order. */
    _queuedLabelsList(ids) {
      return ids.map((id) => this._optionLabelFor(id)).join(', ');
    }
  }

  VaultifyPickers.register('dropdown', DropdownPicker);

  // ── List picker ────────────────────────────────────────────────────────────
  // Vertical stack of selectable option rows (native radio + label), each
  // showing a name, optional secondary detail, and price. Registered for the
  // 'list' picker style (assets/vaultify-pickers.js applies "button" auto-
  // upgrade or explicit custom.picker_style = 'list'). Selection itself is
  // untouched Dawn behaviour — a native radio input's own change event, not
  // intercepted here.
  //
  // Waitlist (Phase 3): sold-out rows become actionable per
  // docs/design/variants/pickers/list.html, "Concept A · adapted" — clicking
  // a sold-out, WAITLIST-eligible row toggles it in the exact same queue
  // DropdownPicker uses, and the exact same shared panel (BasePicker) renders
  // below the fieldset. No new queue, provider, or panel implementation; this
  // class is a renderer over the existing architecture, same as DropdownPicker.
  //
  // Unlike DropdownPicker's fully JS-owned `.dd-option` rows, these radio/
  // label pairs are also owned by SyncVariantVisibility (hiding missing
  // combinations, marking sold-out, sorting, price population) — this class
  // never recreates or reorders those nodes, only toggles waitlist-specific
  // classes/attributes on the existing label in place, so native DOM focus on
  // a row survives a re-render and nothing here can race with
  // SyncVariantVisibility's own DOM ownership.
  class ListPicker extends BasePicker {
    static selector = '.product-form__input--list';

    _init() {
      this.interactionState = VaultifyPickers.getInteractionState(this.wrap);
      this.optionName = this.wrap.dataset.optionName || 'option';

      // Waitlist container + status region are rendered as a sibling of
      // whichever element forms this option's own visible boundary — the
      // fieldset itself in flat layout, or the whole .variant-step card in
      // a journey (see product-variant-picker.liquid: the panel is
      // deliberately rendered after the step wrapper closes, not inside
      // .variant-step__body, so a journey step's bordered card never
      // contains the waitlist panel underneath it). Same dd-waitlist
      // markup/classes DropdownPicker's panel uses, minus Select all/Clear
      // (intentionally omitted for this picker type — see item 6 of the
      // Phase 3 brief: customers click the variants they want individually).
      const stepEl = this.wrap.closest('.variant-step');
      const group = stepEl ? stepEl.nextElementSibling : this.wrap.nextElementSibling;
      const waitlistGroup = group && group.matches('[data-list-waitlist-group]') ? group : null;
      this.waitlistEl = waitlistGroup?.querySelector('[data-dd-waitlist]') || null;
      this.waitlistPanelEl = this.waitlistEl?.querySelector('[data-dd-waitlist-panel]') || null;
      if (this.waitlistPanelEl && !this.waitlistPanelEl.id) {
        this.waitlistPanelEl.id = 'list-waitlist-panel-' + Math.random().toString(36).slice(2, 9);
      }
      this.statusEl = waitlistGroup?.querySelector('[data-dd-waitlist-status]') || null;
      // No Select all/Clear for this picker type — left unset so the shared
      // _renderWaitlistUI()'s null-guards simply no-op for them.
      this.waitlistControlsEl = null;
      this.selectAllBtn = null;
      this.clearWaitlistBtn = null;
      this.waitlistSepEl = null;

      this._submitting = false;
      this._resultState = null;
      this._pendingEmail = null;
      this._panelExpanded = false;
      this._panelDismissed = false;

      this._onRowClick = (e) => {
        const label = e.target.closest('label');
        if (!label || !this.wrap.contains(label)) return;
        const row = this._rows.find((r) => r.label === label);
        if (!row) return;
        const ROW_STATE = VaultifyWaitlistCore.WAITLIST_ROW_STATE;
        const rowState = this._rowWaitlistInfo(row).rowState;
        // Available rows: no interception — the native radio input handles
        // selection itself, exactly as it did before this class existed.
        if (rowState === ROW_STATE.ACTIONABLE || rowState === ROW_STATE.PENDING) {
          this._toggleWaitlistRow(row);
        }
      };
      this.wrap.addEventListener('click', this._onRowClick);

      // Space/Enter parity with the click handler above — only rows marked
      // actionable in _render() (data-waitlist-actionable) are ever given a
      // tabindex, so this never intercepts a purchase-selection keypress.
      this._onRowKeydown = (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const label = e.target.closest('label[data-waitlist-actionable]');
        if (!label || !this.wrap.contains(label)) return;
        e.preventDefault();
        const row = this._rows.find((r) => r.label === label);
        if (row) this._toggleWaitlistRow(row);
      };
      this.wrap.addEventListener('keydown', this._onRowKeydown);

      this._render();
      this._renderWaitlistUI();

      // Re-run on "product-info:loaded" — the same settling signal
      // SyncVariantVisibility relies on — so eligible rows are re-checked
      // after any variant-driven section refresh, not just at construction.
      this._onWaitlistInfoLoaded = () => {
        this._render();
        this._renderWaitlistUI();
      };
      document.addEventListener('product-info:loaded', this._onWaitlistInfoLoaded);

      // Same bounded readiness poll as DropdownPicker — the STOQ provider
      // can still be loading at construction time; waiting for the customer
      // to interact before this area updates would mean they never see the
      // waitlist affordance appear at all. See DropdownPicker._init() for
      // the full reasoning.
      let waitlistReadyPolls = 0;
      const maxWaitlistReadyPolls = 40; // ~8s at 200ms
      this._waitlistReadyPoll = setInterval(() => {
        waitlistReadyPolls++;
        this._renderWaitlistUI();
        const ready = !!VaultifyPickers.getWaitlistProvider()?.isReady();
        if (ready || waitlistReadyPolls >= maxWaitlistReadyPolls) {
          clearInterval(this._waitlistReadyPoll);
          this._waitlistReadyPoll = null;
        }
      }, 200);
    }

    _destroy() {
      this.wrap.removeEventListener('click', this._onRowClick);
      this.wrap.removeEventListener('keydown', this._onRowKeydown);
      if (this._waitlistReadyPoll) clearInterval(this._waitlistReadyPoll);
      document.removeEventListener('product-info:loaded', this._onWaitlistInfoLoaded);
    }

    /**
     * Rebuilds the row model fresh from the live DOM on every render —
     * SyncVariantVisibility can hide/reorder/enable these inputs at any
     * time, so this reads its output rather than caching a stale snapshot
     * (unlike DropdownPicker, which owns its rows exclusively).
     */
    _buildRows() {
      const inputs = Array.from(this.wrap.querySelectorAll('input[type="radio"]'));
      this._rows = inputs
        .filter((input) => !input.hidden)
        .map((input) => {
          const label =
            input.nextElementSibling?.tagName === 'LABEL' ? input.nextElementSibling : input.closest('label');
          return { input, label, value: input.value, available: input.dataset.variantState === 'available' };
        })
        .filter((r) => !!r.label);
      // The generic contract the shared waitlist methods (_optionLabelFor,
      // _eligibleRows, _buildQueueSummary's remove handler…) read.
      this._options = this._rows.map((r) => ({ value: r.value, available: r.available }));
    }

    /**
     * Redraws waitlist visual state onto the existing native radio/label
     * pairs in place — rows are never recreated or reordered here, so
     * keyboard focus on a row survives a re-render.
     */
    _render() {
      this._buildRows();
      const ROW_STATE = VaultifyWaitlistCore.WAITLIST_ROW_STATE;

      this._rows.forEach((row) => {
        const info = this._rowWaitlistInfo(row);
        const label = row.label;

        label.classList.toggle('is-notifiable', !!info.rowState);
        label.classList.toggle('is-queued', info.rowState === ROW_STATE.PENDING);
        label.classList.toggle('is-submitted', info.rowState === ROW_STATE.SUBMITTED);

        const actionable = info.rowState === ROW_STATE.ACTIONABLE || info.rowState === ROW_STATE.PENDING;
        if (actionable) {
          label.setAttribute('data-waitlist-actionable', '');
          label.setAttribute('tabindex', '0');
          label.setAttribute('role', 'button');
          label.setAttribute('aria-pressed', String(info.rowState === ROW_STATE.PENDING));
        } else {
          label.removeAttribute('data-waitlist-actionable');
          label.removeAttribute('tabindex');
          label.removeAttribute('role');
          label.removeAttribute('aria-pressed');
        }

        if (!row.available && info.rowState) {
          const key =
            info.rowState === ROW_STATE.SUBMITTED
              ? 'rowStateSubmitted'
              : info.rowState === ROW_STATE.PENDING
                ? 'rowStateWatching'
                : 'rowStateNotifiable';
          label.setAttribute('aria-label', this._t(key, { option: row.value }));
        } else {
          label.removeAttribute('aria-label');
        }

        this._syncBadge(label, info.rowState);
      });
    }

    // _syncBadge is inherited from BasePicker (promoted here from what was
    // originally ButtonPicker's own method) — see its definition for the
    // full rationale. Previously this method built a dd-tag-wrap text tag
    // directly; replaced when the default row indicator across every
    // picker type became the checkmark badge — see the architecture doc's
    // "Row visual language" note.

    /** Row click/Enter on an eligible sold-out row — identical contract to DropdownPicker's version, minus the dropdown-specific focusedIdx bookkeeping (native DOM focus already stays on the row; see _render()). */
    _toggleWaitlistRow(row) {
      if (!this.interactionState) return;
      this.interactionState.toggleWaitlistVariant(this.optionIdx, row.value);
      this._resultState = null;
      this._render();
      this._renderWaitlistUI();
      const info = this._rowWaitlistInfo(row);
      this._announce(this._t(info.pending ? 'rowStateWatching' : 'rowStateNotifiable', { option: row.value }));
    }
  }

  VaultifyPickers.register('list', ListPicker);

  // ── Button/pill picker ───────────────────────────────────────────────────
  // Same native radio/label ownership model as ListPicker — rows are
  // managed by SyncVariantVisibility (hides missing combinations, marks
  // sold-out, populates prices), never recreated here; this class only
  // toggles waitlist classes/attributes on the existing <label> and reuses
  // the exact same is-notifiable/is-queued/is-submitted vocabulary (plus
  // the shared _syncBadge checkmark indicator, now on BasePicker)
  // ListPicker uses — see ListPicker above for the full rationale, not
  // repeated here since it's the same code, not a parallel rule that
  // happens to match. The row markup product-variant-options.liquid renders
  // for picker_type == 'button' is structurally identical to 'list' (same
  // input + label-with-variant-list__primary/compare-price/price shape),
  // which is what makes this direct a reuse possible.
  //
  // No Select all/Clear for this picker type either — same decision as
  // ListPicker (§14 of the waitlist architecture doc): sold-out pills are
  // clicked individually.
  //
  // Deliberately not merged into one shared class with ListPicker: two
  // consumers of an identical row-toggle pattern is the same count that
  // justified writing the waitlist panel directly on DropdownPicker before
  // a second consumer (ListPicker) existed — this codebase's own
  // established convention promotes shared behaviour to BasePicker once a
  // THIRD consumer needs the identical thing, not preemptively for two.
  // (The row *indicator* specifically — _syncBadge — did cross that
  // threshold once DropdownPicker also needed it and is now on BasePicker;
  // the row-toggle/keyboard-delegation logic below has not.) Segmented
  // (below) is now that third consumer — extraction was considered and
  // deliberately deferred there rather than done here; see SegmentedPicker's
  // own comment for why.
  class ButtonPicker extends BasePicker {
    static selector = '.product-form__input--pill';

    _init() {
      this.interactionState = VaultifyPickers.getInteractionState(this.wrap);
      this.optionName = this.wrap.dataset.optionName || 'option';

      // Same DOM-proximity discovery as ListPicker — see its _init() for
      // the full journey/flat layout reasoning. data-list-waitlist-group
      // and the dd-waitlist* class names are reused unrenamed for this
      // picker type too — see the comment in
      // snippets/product-variant-picker.liquid for why.
      const stepEl = this.wrap.closest('.variant-step');
      const group = stepEl ? stepEl.nextElementSibling : this.wrap.nextElementSibling;
      const waitlistGroup = group && group.matches('[data-list-waitlist-group]') ? group : null;
      this.waitlistEl = waitlistGroup?.querySelector('[data-dd-waitlist]') || null;
      this.waitlistPanelEl = this.waitlistEl?.querySelector('[data-dd-waitlist-panel]') || null;
      if (this.waitlistPanelEl && !this.waitlistPanelEl.id) {
        this.waitlistPanelEl.id = 'button-waitlist-panel-' + Math.random().toString(36).slice(2, 9);
      }
      this.statusEl = waitlistGroup?.querySelector('[data-dd-waitlist-status]') || null;
      // No Select all/Clear for this picker type — left unset so the shared
      // _renderWaitlistUI()'s null-guards simply no-op for them.
      this.waitlistControlsEl = null;
      this.selectAllBtn = null;
      this.clearWaitlistBtn = null;
      this.waitlistSepEl = null;

      this._submitting = false;
      this._resultState = null;
      this._pendingEmail = null;
      this._panelExpanded = false;
      this._panelDismissed = false;

      this._onRowClick = (e) => {
        const label = e.target.closest('label');
        if (!label || !this.wrap.contains(label)) return;
        const row = this._rows.find((r) => r.label === label);
        if (!row) return;
        const ROW_STATE = VaultifyWaitlistCore.WAITLIST_ROW_STATE;
        const rowState = this._rowWaitlistInfo(row).rowState;
        // Available rows: no interception — the native radio input handles
        // selection itself, exactly as it did before this class existed.
        if (rowState === ROW_STATE.ACTIONABLE || rowState === ROW_STATE.PENDING) {
          this._toggleWaitlistRow(row);
        }
      };
      this.wrap.addEventListener('click', this._onRowClick);

      // Space/Enter parity with the click handler above — only rows marked
      // actionable in _render() (data-waitlist-actionable) are ever given a
      // tabindex, so this never intercepts a purchase-selection keypress.
      this._onRowKeydown = (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const label = e.target.closest('label[data-waitlist-actionable]');
        if (!label || !this.wrap.contains(label)) return;
        e.preventDefault();
        const row = this._rows.find((r) => r.label === label);
        if (row) this._toggleWaitlistRow(row);
      };
      this.wrap.addEventListener('keydown', this._onRowKeydown);

      this._render();
      this._renderWaitlistUI();

      // Re-run on "product-info:loaded" — the same settling signal
      // SyncVariantVisibility relies on — so eligible rows are re-checked
      // after any variant-driven section refresh, not just at construction.
      this._onWaitlistInfoLoaded = () => {
        this._render();
        this._renderWaitlistUI();
      };
      document.addEventListener('product-info:loaded', this._onWaitlistInfoLoaded);

      // Same bounded readiness poll as DropdownPicker/ListPicker — the STOQ
      // provider can still be loading at construction time; waiting for the
      // customer to interact before this area updates would mean they never
      // see the waitlist affordance appear at all.
      let waitlistReadyPolls = 0;
      const maxWaitlistReadyPolls = 40; // ~8s at 200ms
      this._waitlistReadyPoll = setInterval(() => {
        waitlistReadyPolls++;
        this._renderWaitlistUI();
        const ready = !!VaultifyPickers.getWaitlistProvider()?.isReady();
        if (ready || waitlistReadyPolls >= maxWaitlistReadyPolls) {
          clearInterval(this._waitlistReadyPoll);
          this._waitlistReadyPoll = null;
        }
      }, 200);
    }

    _destroy() {
      this.wrap.removeEventListener('click', this._onRowClick);
      this.wrap.removeEventListener('keydown', this._onRowKeydown);
      if (this._waitlistReadyPoll) clearInterval(this._waitlistReadyPoll);
      document.removeEventListener('product-info:loaded', this._onWaitlistInfoLoaded);
    }

    /**
     * Rebuilds the row model fresh from the live DOM on every render —
     * SyncVariantVisibility can hide/reorder/enable these inputs at any
     * time, so this reads its output rather than caching a stale snapshot.
     */
    _buildRows() {
      const inputs = Array.from(this.wrap.querySelectorAll('input[type="radio"]'));
      this._rows = inputs
        .filter((input) => !input.hidden)
        .map((input) => {
          const label =
            input.nextElementSibling?.tagName === 'LABEL' ? input.nextElementSibling : input.closest('label');
          return { input, label, value: input.value, available: input.dataset.variantState === 'available' };
        })
        .filter((r) => !!r.label);
      this._options = this._rows.map((r) => ({ value: r.value, available: r.available }));
    }

    /**
     * Redraws waitlist visual state onto the existing native radio/label
     * pairs in place — rows are never recreated or reordered here, so
     * keyboard focus on a row survives a re-render.
     */
    _render() {
      this._buildRows();
      const ROW_STATE = VaultifyWaitlistCore.WAITLIST_ROW_STATE;

      this._rows.forEach((row) => {
        const info = this._rowWaitlistInfo(row);
        const label = row.label;

        label.classList.toggle('is-notifiable', !!info.rowState);
        label.classList.toggle('is-queued', info.rowState === ROW_STATE.PENDING);
        label.classList.toggle('is-submitted', info.rowState === ROW_STATE.SUBMITTED);

        const actionable = info.rowState === ROW_STATE.ACTIONABLE || info.rowState === ROW_STATE.PENDING;
        if (actionable) {
          label.setAttribute('data-waitlist-actionable', '');
          label.setAttribute('tabindex', '0');
          label.setAttribute('role', 'button');
          label.setAttribute('aria-pressed', String(info.rowState === ROW_STATE.PENDING));
        } else {
          label.removeAttribute('data-waitlist-actionable');
          label.removeAttribute('tabindex');
          label.removeAttribute('role');
          label.removeAttribute('aria-pressed');
        }

        if (!row.available && info.rowState) {
          const key =
            info.rowState === ROW_STATE.SUBMITTED
              ? 'rowStateSubmitted'
              : info.rowState === ROW_STATE.PENDING
                ? 'rowStateWatching'
                : 'rowStateNotifiable';
          label.setAttribute('aria-label', this._t(key, { option: row.value }));
        } else {
          label.removeAttribute('aria-label');
        }

        this._syncBadge(label, info.rowState);
      });
    }

    // _syncBadge is inherited from BasePicker — see its definition for the
    // full rationale (promoted there once list/dropdown also needed it).

    /** Row click/Enter on an eligible sold-out row — identical contract to ListPicker's version. */
    _toggleWaitlistRow(row) {
      if (!this.interactionState) return;
      this.interactionState.toggleWaitlistVariant(this.optionIdx, row.value);
      this._resultState = null;
      this._render();
      this._renderWaitlistUI();
      const info = this._rowWaitlistInfo(row);
      this._announce(this._t(info.pending ? 'rowStateWatching' : 'rowStateNotifiable', { option: row.value }));
    }
  }

  VaultifyPickers.register('button', ButtonPicker);

  // ── Segmented picker ─────────────────────────────────────────────────────
  // Same native radio/label ownership model as ListPicker/ButtonPicker —
  // rows are managed by SyncVariantVisibility, never recreated here; this
  // class only toggles waitlist classes/attributes on the existing <label>
  // and reuses the shared is-notifiable/is-queued/is-submitted vocabulary
  // and _syncBadge indicator from BasePicker. The row markup
  // product-variant-options.liquid renders for picker_type == 'segmented'
  // is structurally identical to 'button'/'list' (input + sibling label,
  // data-variant-state, no JS-owned row recreation) — same reuse basis.
  //
  // No Select all/Clear for this picker type either — same decision as
  // ListPicker/ButtonPicker: sold-out segments are clicked individually.
  //
  // This is the third consumer of the identical row-toggle/keyboard-
  // delegation pattern ButtonPicker's own comment predicted as "the point
  // to extract it" — noted, not acted on here. This class is still a
  // duplicate of ButtonPicker/ListPicker's body rather than a shared base
  // class, a deliberate scope call: the task was "add segmented waitlist
  // support," not "refactor three working picker classes," and extracting
  // now would mean changing ListPicker/ButtonPicker's already-shipped,
  // already-tested code for a cleanliness gain the task didn't ask for.
  // If a fourth consumer of this exact pattern appears, that is the point
  // to revisit this deferral, not add a fourth duplicate.
  class SegmentedPicker extends BasePicker {
    static selector = '.product-form__input--segmented';

    _init() {
      this.interactionState = VaultifyPickers.getInteractionState(this.wrap);
      this.optionName = this.wrap.dataset.optionName || 'option';

      // Same DOM-proximity discovery as ListPicker/ButtonPicker — see
      // ListPicker's _init() for the full journey/flat layout reasoning.
      const stepEl = this.wrap.closest('.variant-step');
      const group = stepEl ? stepEl.nextElementSibling : this.wrap.nextElementSibling;
      const waitlistGroup = group && group.matches('[data-list-waitlist-group]') ? group : null;
      this.waitlistEl = waitlistGroup?.querySelector('[data-dd-waitlist]') || null;
      this.waitlistPanelEl = this.waitlistEl?.querySelector('[data-dd-waitlist-panel]') || null;
      if (this.waitlistPanelEl && !this.waitlistPanelEl.id) {
        this.waitlistPanelEl.id = 'segmented-waitlist-panel-' + Math.random().toString(36).slice(2, 9);
      }
      this.statusEl = waitlistGroup?.querySelector('[data-dd-waitlist-status]') || null;
      this.waitlistControlsEl = null;
      this.selectAllBtn = null;
      this.clearWaitlistBtn = null;
      this.waitlistSepEl = null;

      this._submitting = false;
      this._resultState = null;
      this._pendingEmail = null;
      this._panelExpanded = false;
      this._panelDismissed = false;

      this._onRowClick = (e) => {
        const label = e.target.closest('label');
        if (!label || !this.wrap.contains(label)) return;
        const row = this._rows.find((r) => r.label === label);
        if (!row) return;
        const ROW_STATE = VaultifyWaitlistCore.WAITLIST_ROW_STATE;
        const rowState = this._rowWaitlistInfo(row).rowState;
        if (rowState === ROW_STATE.ACTIONABLE || rowState === ROW_STATE.PENDING) {
          this._toggleWaitlistRow(row);
        }
      };
      this.wrap.addEventListener('click', this._onRowClick);

      this._onRowKeydown = (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const label = e.target.closest('label[data-waitlist-actionable]');
        if (!label || !this.wrap.contains(label)) return;
        e.preventDefault();
        const row = this._rows.find((r) => r.label === label);
        if (row) this._toggleWaitlistRow(row);
      };
      this.wrap.addEventListener('keydown', this._onRowKeydown);

      this._render();
      this._renderWaitlistUI();

      this._onWaitlistInfoLoaded = () => {
        this._render();
        this._renderWaitlistUI();
      };
      document.addEventListener('product-info:loaded', this._onWaitlistInfoLoaded);

      let waitlistReadyPolls = 0;
      const maxWaitlistReadyPolls = 40;
      this._waitlistReadyPoll = setInterval(() => {
        waitlistReadyPolls++;
        this._renderWaitlistUI();
        const ready = !!VaultifyPickers.getWaitlistProvider()?.isReady();
        if (ready || waitlistReadyPolls >= maxWaitlistReadyPolls) {
          clearInterval(this._waitlistReadyPoll);
          this._waitlistReadyPoll = null;
        }
      }, 200);
    }

    _destroy() {
      this.wrap.removeEventListener('click', this._onRowClick);
      this.wrap.removeEventListener('keydown', this._onRowKeydown);
      if (this._waitlistReadyPoll) clearInterval(this._waitlistReadyPoll);
      document.removeEventListener('product-info:loaded', this._onWaitlistInfoLoaded);
    }

    _buildRows() {
      const inputs = Array.from(this.wrap.querySelectorAll('input[type="radio"]'));
      this._rows = inputs
        .filter((input) => !input.hidden)
        .map((input) => {
          const label =
            input.nextElementSibling?.tagName === 'LABEL' ? input.nextElementSibling : input.closest('label');
          return { input, label, value: input.value, available: input.dataset.variantState === 'available' };
        })
        .filter((r) => !!r.label);
      this._options = this._rows.map((r) => ({ value: r.value, available: r.available }));
    }

    _render() {
      this._buildRows();
      const ROW_STATE = VaultifyWaitlistCore.WAITLIST_ROW_STATE;

      this._rows.forEach((row) => {
        const info = this._rowWaitlistInfo(row);
        const label = row.label;

        label.classList.toggle('is-notifiable', !!info.rowState);
        label.classList.toggle('is-queued', info.rowState === ROW_STATE.PENDING);
        label.classList.toggle('is-submitted', info.rowState === ROW_STATE.SUBMITTED);

        const actionable = info.rowState === ROW_STATE.ACTIONABLE || info.rowState === ROW_STATE.PENDING;
        if (actionable) {
          label.setAttribute('data-waitlist-actionable', '');
          label.setAttribute('tabindex', '0');
          label.setAttribute('role', 'button');
          label.setAttribute('aria-pressed', String(info.rowState === ROW_STATE.PENDING));
        } else {
          label.removeAttribute('data-waitlist-actionable');
          label.removeAttribute('tabindex');
          label.removeAttribute('role');
          label.removeAttribute('aria-pressed');
        }

        if (!row.available && info.rowState) {
          const key =
            info.rowState === ROW_STATE.SUBMITTED
              ? 'rowStateSubmitted'
              : info.rowState === ROW_STATE.PENDING
                ? 'rowStateWatching'
                : 'rowStateNotifiable';
          label.setAttribute('aria-label', this._t(key, { option: row.value }));
        } else {
          label.removeAttribute('aria-label');
        }

        this._syncBadge(label, info.rowState);
      });
    }

    /** Row click/Enter on an eligible sold-out row — identical contract to ListPicker's/ButtonPicker's version. */
    _toggleWaitlistRow(row) {
      if (!this.interactionState) return;
      this.interactionState.toggleWaitlistVariant(this.optionIdx, row.value);
      this._resultState = null;
      this._render();
      this._renderWaitlistUI();
      const info = this._rowWaitlistInfo(row);
      this._announce(this._t(info.pending ? 'rowStateWatching' : 'rowStateNotifiable', { option: row.value }));
    }
  }

  VaultifyPickers.register('segmented', SegmentedPicker);

  // ── Stop track picker ─────────────────────────────────────────────────────
  // Ordered quantity / tier scale picker. Registered for the stop_track
  // picker style. Works standalone or as a step in a journey. Uses native radio inputs.

  class StopTrackPicker extends BasePicker {
    static selector = '.product-form__input--stop-track';

    _init() {
      this._update();
      this._populatePrices();
    }

    // Sync is-past / is-selected classes and --fill-pct from current radio state.
    _update() {
      const inputs = Array.from(this.wrap.querySelectorAll('input[type="radio"]'));
      const stops = Array.from(this.wrap.querySelectorAll('.stop-track__stop'));
      if (!stops.length) return;

      const checkedIdx = inputs.findIndex((i) => i.checked);
      stops.forEach((stop, i) => {
        stop.classList.toggle('is-past', i < checkedIdx);
        stop.classList.toggle('is-selected', i === checkedIdx);
      });

      const track = this.wrap.querySelector('.stop-track');
      if (track && stops.length > 1) {
        const pct = checkedIdx >= 0 ? (checkedIdx / (stops.length - 1)) * 100 : 0;
        track.style.setProperty('--fill-pct', pct + '%');
      }
    }

    _populatePrices() {
      const inputs = Array.from(this.wrap.querySelectorAll('input[type="radio"]'));
      const stops = Array.from(this.wrap.querySelectorAll('.stop-track__stop'));
      inputs.forEach((input, i) => {
        const priceEl = stops[i]?.querySelector('.stop-track__price');
        if (!priceEl) return;
        const cents = this._getPriceCents(input.value);
        if (cents !== null) priceEl.textContent = this._formatPrice(cents);
      });
    }
  }

  VaultifyPickers.register('stop_track', StopTrackPicker);

  // Delegated change listener — single listener handles all stop-track
  // fieldsets on the page. Native radio change events are always isTrusted
  // so no _vaultifyInternal flag needed here.
  document.addEventListener('change', (e) => {
    const fieldset = e.target.closest('.product-form__input--stop-track');
    if (fieldset?._vaultifyPicker) fieldset._vaultifyPicker._update();
  });

  // ── Journey orchestrator ───────────────────────────────────────────────────
  // Manages multi-step variant selection when data-picker-mode="journey" is set
  // on <variant-selects>. Controls step visibility, advances on selection, and
  // allows back-navigation to earlier steps.
  //
  // pickers: Map of { pickerType → pickerInstance } — available for future use.

  class JourneyOrchestrator {
    constructor(vs, pickers) {
      this.vs = vs;
      this.root = vs.closest('product-info') || vs.closest('[id^="MainProduct-"]');
      if (!this.root) return;

      // _journeyAllDone persists on root across orchestrator instances (survives reinit)
      if (this.root._journeyAllDone === undefined) this.root._journeyAllDone = false;

      this._onClick = (e) => this._handleClick(e);
      this._onChange = (e) => this._handleChange(e);
      this._onKeydown = (e) => this._handleKeydown(e);
      this.root.addEventListener('click', this._onClick);
      this.root.addEventListener('change', this._onChange);
      this.root.addEventListener('keydown', this._onKeydown);

      this._relocateSelectionSummary();

      // Double-rAF: runs after Frame 1 option-filtering in the variantChange boot handler.
      // On initial load this is a harmless two-frame defer; after section fetches it ensures
      // syncVariantVisibility has settled before we restore step states.
      requestAnimationFrame(() => requestAnimationFrame(() => this.initSteps()));
    }

    /**
     * Moves the selection summary (rendered by product-variant-picker.liquid
     * as a sibling of <variant-selects>, for simple, journey-mode-gated
     * markup) to sit immediately BEFORE the price block instead — "just
     * above the price" is where a customer actually needs this context,
     * not after the full step list where it's easy to miss and far from
     * the number it explains. Found via the price block's own
     * id="price-{{ section.id }}" wrapper (sections/main-product-v2.liquid)
     * rather than a hardcoded CSS order, since block order itself is
     * merchant-configurable in the Theme Editor — this works regardless of
     * where the merchant has placed the price/variant_picker blocks
     * relative to each other. Falls back to leaving it where Liquid
     * rendered it if no price element is found (e.g. a non-standard
     * section layout) — better than losing the summary entirely.
     *
     * Searches this.root broadly rather than this.vs.nextElementSibling —
     * a real selection triggers Dawn's own section fetch, which recreates
     * this JourneyOrchestrator (see VaultifyPickers.reinit()), and by then
     * the element is no longer literally variant-selects' next sibling
     * (this method already moved it once). A narrower lookup here silently
     * stopped finding it after the very first relocation, which is why the
     * summary text stopped updating on later real selections while still
     * updating correctly during exploration (which never triggers a
     * fetch/reinit at all). Also cleans up a stale duplicate if Dawn's
     * fetch happened to re-render a fresh copy at its original position
     * while the previously-relocated one was left untouched outside the
     * replaced fragment — keeps whichever is LAST in document order, since
     * a freshly re-rendered copy (still at its natural post-variant-selects
     * position) always sorts after an already-relocated one (moved up next
     * to price, near the top of the info column).
     */
    _relocateSelectionSummary() {
      const candidates = Array.from(this.root.querySelectorAll('[data-journey-selection-summary]'));
      if (!candidates.length) return;
      const el = candidates[candidates.length - 1];
      candidates.slice(0, -1).forEach((dup) => dup.remove());
      this._selectionSummaryEl = el;
      const priceEl = this.root.querySelector('[id^="price-"]');
      if (priceEl) priceEl.insertAdjacentElement('beforebegin', el);
    }

    destroy() {
      if (!this.root) return;
      this.root.removeEventListener('click', this._onClick);
      this.root.removeEventListener('change', this._onChange);
      this.root.removeEventListener('keydown', this._onKeydown);
    }

    _steps() {
      return Array.from(this.vs.querySelectorAll('.variant-step'));
    }

    /** True when this step's underlying radio group or select already has a genuine value — used to decide whether re-opening an EARLIER step should leave a later "done" step alone (see _handleClick's doneHeader branch). */
    _hasValue(step) {
      if (step.querySelector('input[type="radio"]:checked')) return true;
      const select = step.querySelector('select.select__select');
      return !!(select && select.value);
    }

    setDone(step, value) {
      step.classList.remove('variant-step--active', 'variant-step--locked');
      step.classList.add('variant-step--done');
      const chosenEl = step.querySelector('.variant-step__chosen');
      if (chosenEl && value) chosenEl.textContent = value;
      const numEl = step.querySelector('.variant-step__num');
      if (numEl) numEl.textContent = '✓';
      const header = step.querySelector('.variant-step__header');
      if (header) {
        header.setAttribute('role', 'button');
        header.setAttribute('tabindex', '0');
      }
    }

    setActive(step) {
      step.classList.remove('variant-step--done', 'variant-step--locked');
      step.classList.add('variant-step--active');
      const numEl = step.querySelector('.variant-step__num');
      if (numEl && numEl.dataset.stepNum) numEl.textContent = numEl.dataset.stepNum;
      const header = step.querySelector('.variant-step__header');
      if (header) {
        header.removeAttribute('role');
        header.removeAttribute('tabindex');
      }
    }

    setLocked(step) {
      step.classList.remove('variant-step--done', 'variant-step--active');
      step.classList.add('variant-step--locked');
      const header = step.querySelector('.variant-step__header');
      if (header) {
        header.removeAttribute('role');
        header.removeAttribute('tabindex');
      }
    }

    initSteps() {
      const steps = this._steps();
      if (steps.length < 2) return;

      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        const isLast = i === steps.length - 1;
        // Server-rendered .variant-step__chosen is the most reliable source across initial
        // load and post-fetch reinits. Radio checked and select value are fallbacks.
        const value =
          step.querySelector('.variant-step__chosen')?.textContent?.trim() ||
          step.querySelector('input[type="radio"]:checked')?.value ||
          step.querySelector('select.select__select')?.value ||
          null;

        if (!isLast) {
          if (value) {
            this.setDone(step, value);
          } else {
            this.setActive(step);
            steps.slice(i + 1).forEach((s) => this.setLocked(s));
            this._updateHints();
            this._updateSelectionSummary();
            return;
          }
        } else {
          // Last step: keep done if the customer already completed the journey once.
          // Prevents re-opening the last step after a section fetch triggered by their selection.
          if (this.root._journeyAllDone && value) {
            this.setDone(step, value);
          } else {
            this.setActive(step);
          }
        }
      }
      this._updateHints();
      this._updateSelectionSummary();
    }

    /**
     * Keeps the customer's actual selected variant path visible above the
     * price no matter which step they're currently navigating or
     * exploring — see the journey selection summary comment in
     * product-variant-picker.liquid and the waitlist architecture doc §16.
     * Shown whenever a real variant is resolved, not only while exploring
     * away from it — reads naturally as a caption for the price directly
     * below it either way. No price of its own (the actual price element
     * sits right underneath) and no "Selected:" framing — just the
     * variant path (Shopify's own variant.title, e.g.
     * "No Retail Box / 20 Toploaders").
     *
     * Deliberately reads Dawn's own hidden input[name="id"] rather than
     * ProductInteractionState.getResolvedVariant()/_currentSelections():
     * the latter recomputes live from whatever radios are currently
     * checked, which — during a branch EXPLORATION (Phase X) — can
     * include a value that was never a real purchase selection (checked
     * without a 'change' event). input[name="id"] only ever changes via
     * Dawn's own real, 'change'-driven section fetch, so it's the one
     * value that's immune to exploration and always matches what's
     * actually in the product form / would actually be added to cart.
     */
    _updateSelectionSummary() {
      const el = this._selectionSummaryEl;
      if (!el) return;

      const interactionState = VaultifyPickers.getInteractionState(this.vs);
      const idInput = this.root?.querySelector('input[name="id"]');
      const variant = interactionState && idInput ? interactionState.getVariantById(idInput.value) : null;

      if (!variant) {
        el.hidden = true;
        el.textContent = '';
        return;
      }

      el.hidden = false;
      el.textContent = variant.title;
    }

    _updateHints() {
      if (!window.productData?.variants) return;
      const steps = this._steps();

      const upstream = {};
      steps.forEach((s) => {
        if (!s.classList.contains('variant-step--done')) return;
        const optIdx = parseInt(s.dataset.stepIndex, 10);
        const val = s.querySelector('.variant-step__chosen')?.textContent?.trim();
        if (!isNaN(optIdx) && val) upstream[optIdx] = val;
      });

      steps.forEach((s) => {
        if (!s.classList.contains('variant-step--locked')) return;
        const hintEl = s.querySelector('.variant-step__hint');
        if (!hintEl) return;

        const compatible = window.productData.variants.filter((v) =>
          Object.entries(upstream).every(([i, val]) => v['option' + (parseInt(i, 10) + 1)] === val),
        );

        if (!compatible.length) {
          hintEl.textContent = '';
          return;
        }

        const pool = compatible.filter((v) => v.available).length ? compatible.filter((v) => v.available) : compatible;
        const prices = pool.map((v) => v.price);
        const min = Math.min(...prices);
        const max = Math.max(...prices);

        hintEl.textContent = min === max ? '£' + (min / 100).toFixed(2) : 'From £' + (min / 100).toFixed(2);
      });
    }

    _advance(steps, idx, value) {
      this.setDone(steps[idx], value);
      const next = steps[idx + 1];
      if (next) {
        this.setActive(next);
      } else {
        this.root._journeyAllDone = true;
      }
      this._updateHints();
      // Idempotent for a real selection (input[name="id"] hasn't changed
      // yet at this point — Dawn's section fetch is still in flight; the
      // reinit it triggers re-runs initSteps(), which calls this again
      // once the fetch has actually updated it). For an exploration
      // advance, input[name="id"] never changes at all, so this simply
      // re-confirms the summary is still showing the real selection,
      // untouched by whatever branch was just explored.
      this._updateSelectionSummary();
    }

    /**
     * Syncs the step's underlying native form control to `value` so
     * ProductInteractionState._currentSelections() (which reads
     * :checked/select.value directly, not via events) reflects the
     * explored branch for the NEXT step's resolution — without
     * dispatching 'change'. Deliberately renderer-agnostic: works for a
     * radio group (list/segmented/pill/stop-track/swatch — all share the
     * same input[type="radio"][value] shape) and for a hidden
     * select.select__select (dropdown), covering every current and future
     * journey-step renderer without picker-specific code here.
     *
     * Not dispatching 'change' is the whole point — Dawn's own
     * VariantSelects listener only reacts to that event, so this can never
     * trigger a section fetch, price/ATC update, or URL change. It is not
     * a purchase selection; it exists purely so later-step candidate
     * resolution (getBranchState/resolveCandidate) sees the branch the
     * customer is exploring.
     */
    _exploreBranch(step, value) {
      const input = step.querySelector('input[type="radio"][value="' + CSS.escape(value) + '"]');
      if (input) {
        input.checked = true;
      } else {
        const select = step.querySelector('select.select__select');
        if (select) select.value = value;
      }
      // Signals SyncVariantVisibility/DropdownPicker/ListPicker to re-sync
      // their own rendering against the new (exploration-only) selection —
      // the same settling event they already listen for after a real
      // section fetch, but nothing here triggers one.
      document.dispatchEvent(new CustomEvent('product-info:loaded'));

      // A second, rAF-deferred dispatch is required, not optional:
      // SyncVariantVisibility's own _sync() is itself scheduled via its
      // own requestAnimationFrame (see _schedule()) — it hasn't run yet
      // when the dispatch above returns. A later step's row that only
      // becomes relevant under the branch just explored (e.g. a Bundle
      // value only valid under "With Retail Box") was hidden as "missing"
      // while a different Packaging value was selected, and stays hidden
      // until SyncVariantVisibility's deferred pass un-hides it.
      // ListPicker/DropdownPicker's own _render() runs *synchronously* off
      // the first dispatch and filters out anything still marked hidden —
      // confirmed live: every row correctly resolved to WAITLIST-eligible,
      // but none got the is-notifiable pill because they were all still
      // filtered out as hidden at that point. requestAnimationFrame here
      // schedules after SyncVariantVisibility's own already-pending frame
      // (its _schedule() call above happens synchronously, before this
      // one, so its rAF request was queued first) — same "wait one frame
      // for SyncVariantVisibility to settle" pattern already used in this
      // class's own constructor.
      requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('product-info:loaded'));
      });
    }

    _handleClick(e) {
      // Re-open a done step by clicking its header
      const doneHeader = e.target.closest('.variant-step__header[role="button"]');
      if (doneHeader) {
        const step = doneHeader.closest('.variant-step');
        if (step?.classList.contains('variant-step--done')) {
          this.root._journeyAllDone = false;
          const steps = this._steps();
          const idx = steps.indexOf(step);
          this.setActive(step);
          // Only lock a LATER step if it doesn't already have a genuine
          // value of its own — reopening an earlier step to browse/change
          // it must never silently forget a real choice made further down
          // the journey. A later step's underlying radio/select is
          // untouched by reopening an earlier one (nothing here checks or
          // unchecks anything), so if it still has a value, it's still the
          // real selection; leave it exactly as-is (still "done", still
          // showing that value) rather than blanking it back to "locked".
          steps.slice(idx + 1).forEach((s) => {
            if (!this._hasValue(s)) this.setLocked(s);
          });
          this._updateHints();
        }
        return;
      }

      // Journey branch exploration (Phase X): a sold-out EARLIER step that
      // still contains a WAITLIST-eligible descendant deeper in the
      // journey (data-branch-explorable, set by SyncVariantVisibility —
      // see getBranchState()). Renderer-agnostic on purpose: any clickable
      // element carrying this marker is handled identically here, whether
      // it's a radio <label> (list/segmented/pill) or a future non-label
      // renderer — the Journey engine decides whether a branch is
      // explorable, individual pickers only render the state.
      //
      // This is EXPLORATION, not purchase selection: _exploreBranch()
      // syncs the underlying form control for OUR OWN later resolution
      // (getBranchState/resolveCandidate for the next step) without
      // dispatching a 'change' event, so Dawn's own VariantSelects
      // listener never runs — no section fetch, no price/ATC/URL update,
      // no purchase-state transition. Only once the journey reaches an
      // exact variant does the existing (unchanged) waitlist/purchase
      // machinery take over.
      const explorable = e.target.closest('[data-branch-explorable]');
      if (explorable) {
        const step = explorable.closest('.variant-step--active');
        if (!step) return;
        const value = explorable.dataset.branchValue;
        if (!value) return;
        this._exploreBranch(step, value);
        const steps = this._steps();
        const idx = steps.indexOf(step);
        this._advance(steps, idx, value);
        return;
      }

      // Advance on radio label click (button, segmented, stop-track pickers).
      // Using click rather than change means re-clicking the already-selected option
      // also advances — change does not fire when re-selecting the same radio.
      const label = e.target.closest('label');
      if (!label) return;
      // A sold-out row's label in the list picker (ListPicker) is clickable
      // for waitlist toggling, not variant selection — its own radio input
      // is disabled and never becomes checked. Without this guard, clicking
      // it still found *whatever radio was already checked* in the step
      // after the rAF tick below and incorrectly advanced/collapsed the
      // step using that stale selection, even though nothing about the
      // actual purchase selection changed.
      if (label.hasAttribute('data-waitlist-actionable')) return;
      const step = label.closest('.variant-step--active');
      if (!step) return;
      const steps = this._steps();
      const idx = steps.indexOf(step);
      // rAF so the browser updates input.checked before we read it
      requestAnimationFrame(() => {
        const checked = step.querySelector('input[type="radio"]:checked');
        if (!checked) return;
        this._advance(steps, idx, checked.value);
      });
    }

    _handleChange(e) {
      // Advance on dropdown selection. DropdownPicker._pick() dispatches change on the
      // hidden select.select__select — that's the signal to advance the step.
      if (!e.target.classList.contains('select__select')) return;
      const step = e.target.closest('.variant-step--active');
      if (!step || !e.target.value) return;
      const steps = this._steps();
      const idx = steps.indexOf(step);
      this._advance(steps, idx, e.target.value);
    }

    _handleKeydown(e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const header = e.target.closest('.variant-step__header[role="button"]');
      if (header) {
        e.preventDefault();
        header.click();
        return;
      }
      // Waitlist-explorable branches get tabindex="0"/role="button" from
      // SyncVariantVisibility — Space/Enter parity with click, routed
      // through the same _handleClick() path rather than duplicated here.
      const explorable = e.target.closest('[data-branch-explorable]');
      if (explorable) {
        e.preventDefault();
        explorable.click();
      }
    }
  }

  VaultifyPickers.registerOrchestrator('journey', JourneyOrchestrator);

  // ── SyncVariantVisibility ──────────────────────────────────────────────────
  // Manages list/button picker option state for a single product section:
  //   - hides options whose combination is completely missing from the product
  //   - marks sold-out options with is-sold-out
  //   - sorts available options before sold-out, inserts a separator
  //   - populates price ranges on list picker labels inside journey step bodies
  //
  // Constructed from a DOMContentLoaded callback in the section (DOM is ready).
  // Exposed on VaultifyPickers so the section can call:
  //   new VaultifyPickers.SyncVariantVisibility(sectionId)

  class SyncVariantVisibility {
    constructor(sectionId) {
      const product = window.productData;
      if (!product || !Array.isArray(product.variants) || !product.variants.length) return;

      this._root = '#MainProduct-' + sectionId;
      this._product = product;
      this._rafId = 0;

      // Named handlers so destroy() can remove them.
      this._onInfoLoaded = () => this._schedule();
      this._onChange = (e) => {
        const r = this._getRoot();
        if (!r || !r.contains(e.target)) return;
        if (e.target.matches('variant-selects input[type="radio"]') || e.target.matches('variant-selects select'))
          this._schedule();
      };

      // Early sort using server-rendered data-variant-state so the separator
      // appears immediately on paint, before the first rAF sync runs.
      const root = this._getRoot();
      if (root) this._fieldsets(root).forEach((f) => this._sortFieldset(f));

      // Re-sort after section fetches (Dawn replaces variant-selects innerHTML).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsubVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          const r = this._getRoot();
          if (r) this._fieldsets(r).forEach((f) => this._sortFieldset(f));
        });
      }

      this._schedule();

      document.addEventListener('product-info:loaded', this._onInfoLoaded);
      document.addEventListener('change', this._onChange);
    }

    destroy() {
      cancelAnimationFrame(this._rafId);
      document.removeEventListener('product-info:loaded', this._onInfoLoaded);
      document.removeEventListener('change', this._onChange);
      this._unsubVariantChange?.();
    }

    _getRoot() {
      return document.querySelector(this._root);
    }
    _fieldsets(root) {
      return Array.from(root.querySelectorAll('variant-selects fieldset'));
    }

    _selections(root) {
      return this._fieldsets(root).map((fs) => {
        const checked = fs.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
        const sel = fs.querySelector('select');
        return sel ? sel.value : '';
      });
    }

    _priorMatch(variant, selections, upTo) {
      for (let i = 0; i < upTo; i++) {
        if (selections[i] && variant.options[i] !== selections[i]) return false;
      }
      return true;
    }

    _status(optIdx, value, selections) {
      const matching = this._product.variants.filter(
        (v) => v.options[optIdx] === value && this._priorMatch(v, selections, optIdx),
      );
      if (!matching.length) return 'missing';
      return matching.some((v) => v.available) ? 'available' : 'sold-out';
    }

    // Prior + subsequent context — used for pickers that show all options simultaneously
    // (e.g. segmented). Does not depend on input.disabled being set by uvos first.
    _statusBidirectional(optIdx, value, selections) {
      const prior = selections.slice(0, optIdx);
      const subsequent = selections.slice(optIdx + 1);
      const matchesPossible = this._product.variants.some(
        (v) => v.options[optIdx] === value && prior.every((s, i) => !s || v.options[i] === s),
      );
      if (!matchesPossible) return 'missing';
      const matchesAvailable = this._product.variants.some(
        (v) =>
          v.available &&
          v.options[optIdx] === value &&
          prior.every((s, i) => !s || v.options[i] === s) &&
          subsequent.every((s, i) => !s || v.options[optIdx + 1 + i] === s),
      );
      return matchesAvailable ? 'available' : 'sold-out';
    }

    _labelFor(input) {
      const next = input.nextElementSibling;
      if (next && next.tagName === 'LABEL') return next;
      return input.closest('label');
    }

    _fmt(cents) {
      return '£' + (typeof cents === 'number' ? cents / 100 : parseFloat(cents)).toFixed(2);
    }

    _updatePrice(input, optIdx, selections) {
      const label = this._labelFor(input);
      if (!label || !label.closest('.variant-step__body')) return;
      const priceEl = label.querySelector('.variant-list__price');
      const compareEl = label.querySelector('.variant-list__compare-price');
      if (!priceEl) return;

      const matches = this._product.variants.filter(
        (v) => v.options[optIdx] === input.value && this._priorMatch(v, selections, optIdx),
      );
      if (!matches.length) {
        priceEl.textContent = '';
        if (compareEl) compareEl.textContent = '';
        return;
      }

      // No available variant at this value — show the actual price of the
      // matching (sold-out) variant(s) rather than a literal "Sold out"
      // string. The row's own styling (dashed border, reduced opacity, the
      // "Sold out" separator/tag) already communicates unavailability;
      // repeating it as the price text just removes useful information —
      // especially for a waitlist-eligible row, where the price is exactly
      // what the customer is deciding whether to wait for.
      const pool = matches.filter((v) => v.available).length ? matches.filter((v) => v.available) : matches;
      const min = Math.min(...pool.map((v) => v.price));
      const max = Math.max(...pool.map((v) => v.price));

      if (min === max) {
        priceEl.textContent = this._fmt(min);
        if (compareEl) {
          const v = pool.find((v) => v.price === min);
          compareEl.textContent =
            v.compare_at_price && v.compare_at_price > v.price ? this._fmt(v.compare_at_price) : '';
        }
      } else {
        priceEl.textContent = this._fmt(min) + ' – ' + this._fmt(max);
        if (compareEl) compareEl.textContent = '';
      }
    }

    /**
     * True when this fieldset's step (if any) is the LAST step in its
     * journey, or when it isn't inside a journey step at all (flat layout,
     * or a parallel non-stepped option) — in both of those cases the axis
     * is where an exact variant is actually resolved, so a sold-out value
     * here is a genuine waitlist ROW (owned by the picker itself — e.g.
     * ListPicker's is-notifiable), not a branch to explore further. Only a
     * sold-out value on an EARLIER step can be a "branch" in the Phase X
     * sense — see _sync()'s branch-explorable classification below.
     */
    _isFinalStep(fieldset) {
      const step = fieldset.closest('.variant-step');
      if (!step) return true;
      const steps = Array.from(fieldset.closest('variant-selects')?.querySelectorAll('.variant-step') || []);
      return steps.length > 0 && steps[steps.length - 1] === step;
    }

    _isDawnNative(fieldset) {
      return (
        fieldset.classList.contains('product-form__input--swatch') ||
        fieldset.classList.contains('product-form__input--vaultify-swatch')
      );
    }

    _sortFieldset(fieldset) {
      if (fieldset.classList.contains('product-form__input--stop-track')) return;
      if (fieldset.classList.contains('product-form__input--segmented')) return;
      if (this._isDawnNative(fieldset)) return;

      fieldset.querySelector('.variant-step__separator')?.remove();

      const order = { available: 0, 'sold-out': 1, missing: 2 };
      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const pairs = inputs.map((input) => {
        let state = input.dataset.variantState;
        if (state === 'available' && (input.disabled || input.classList.contains('disabled'))) {
          state = 'sold-out';
        }
        return { input, label: this._labelFor(input), priority: order[state] ?? 0 };
      });

      pairs.sort((a, b) => a.priority - b.priority);
      pairs.forEach(({ input, label, priority }) => {
        fieldset.appendChild(input);
        if (label) {
          fieldset.appendChild(label);
          label.classList.toggle('is-sold-out', priority === 1);
        }
      });

      const hasAvail = pairs.some((p) => p.priority === 0);
      const firstUnavail = pairs.find((p) => p.priority === 1);
      if (hasAvail && firstUnavail) {
        const sep = document.createElement('div');
        sep.className = 'variant-step__separator';
        sep.textContent = 'Sold out';
        sep.setAttribute('aria-hidden', 'true');
        fieldset.insertBefore(sep, firstUnavail.input);
      }
    }

    _sync() {
      const root = this._getRoot();
      if (!root) return;

      const selections = this._selections(root);

      this._fieldsets(root).forEach((fieldset, optIdx) => {
        // Dawn-native pickers (swatch, pill, variant-group) manage their own state
        // via updateVariantOptionStates(). Don't touch them.
        if (this._isDawnNative(fieldset)) return;

        const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));

        const isSegmented = fieldset.classList.contains('product-form__input--segmented');

        radios.forEach((input) => {
          const label = this._labelFor(input);
          // Segmented pickers show all options simultaneously — use bidirectional context
          // so cross-filter is computed directly without relying on input.disabled being
          // set by updateVariantOptionStates() first (rAF ordering is not guaranteed in
          // the modal context on first open).
          let status = isSegmented
            ? this._statusBidirectional(optIdx, input.value, selections)
            : this._status(optIdx, input.value, selections);

          // For non-segmented pickers: if uvos has already disabled this input via
          // bidirectional context and SVV only sees it as globally available (prior-only),
          // defer to the JS-managed disabled state.
          if (!isSegmented && status === 'available' && input.disabled) status = 'sold-out';
          input.dataset.variantState = status;

          if (status === 'missing') {
            input.disabled = true;
            input.hidden = true;
            if (label) {
              label.hidden = true;
              label.classList.remove('is-sold-out', 'is-branch-explorable');
              label.classList.add('is-missing');
              label.removeAttribute('data-branch-explorable');
              label.removeAttribute('data-branch-value');
            }
            return;
          }
          input.hidden = false;

          // Journey branch exploration (Phase X): a sold-out EARLIER step
          // (see _isFinalStep) may still contain a WAITLIST-eligible
          // descendant further down the journey. getBranchState() (from
          // ProductInteractionState, the sole Purchase State authority —
          // not a new eligibility system) answers that; the FINAL step's
          // own sold-out rows are left to the picker's existing waitlist-
          // row handling (e.g. ListPicker's is-notifiable) untouched.
          let isBranchExplorable = false;
          if (status === 'sold-out' && !this._isFinalStep(fieldset)) {
            const interactionState = VaultifyPickers.getInteractionState(fieldset);
            const branchState = interactionState?.getBranchState(optIdx, input.value);
            isBranchExplorable = branchState === VaultifyWaitlistCore.BRANCH_STATE.WAITLIST_EXPLORABLE;
          }

          if (label) {
            label.hidden = false;
            label.classList.toggle('is-sold-out', status === 'sold-out');
            label.classList.toggle('is-branch-explorable', isBranchExplorable);
            label.classList.remove('is-missing');
            if (isBranchExplorable) {
              label.setAttribute('data-branch-explorable', '');
              label.setAttribute('data-branch-value', input.value);
              // Keyboard reachable + announced distinctly from a plain
              // disabled control — pointer-events is re-enabled for this
              // class in CSS (component-vaultify-pickers.css), same
              // pattern as ListPicker's data-waitlist-actionable rows.
              label.setAttribute('tabindex', '0');
              label.setAttribute('role', 'button');
              const template = window.waitlistStrings?.branchExplorable;
              if (template) {
                label.setAttribute(
                  'aria-label',
                  VaultifyWaitlistCore.formatTemplate(template, { option: input.value }),
                );
              }
            } else {
              label.removeAttribute('data-branch-explorable');
              label.removeAttribute('data-branch-value');
              label.removeAttribute('tabindex');
              label.removeAttribute('role');
              label.removeAttribute('aria-label');
            }
          }
          this._updatePrice(input, optIdx, selections);

          // Segmented pickers use .seg-price rather than .variant-list__price; the
          // _updatePrice() early-return means it never fills the segmented slot — set it here.
          if (fieldset.classList.contains('product-form__input--segmented') && status === 'sold-out') {
            const priceEl = label?.querySelector('.seg-price');
            if (priceEl) priceEl.textContent = 'Sold out';
          }
        });

        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked?.hidden) {
          const first = radios.find((r) => !r.hidden && !r.disabled);
          if (first) {
            first.checked = true;
            const correctionEvent = new Event('change', { bubbles: true });
            correctionEvent._vaultifyInternal = true;
            first.dispatchEvent(correctionEvent);
          }
        }

        this._sortFieldset(fieldset);
      });
    }

    _schedule() {
      cancelAnimationFrame(this._rafId);
      this._rafId = requestAnimationFrame(() => this._sync());
    }
  }

  VaultifyPickers.SyncVariantVisibility = SyncVariantVisibility;

  // ── Swatch picker ──────────────────────────────────────────────────────────
  // Circular colour/material discs. Registered for the 'swatch' picker style —
  // applies when the option has colour/image swatch data (auto-detected) or
  // custom.picker_style = 'swatch' (explicit). Uses native radio inputs so
  // selection events are isTrusted — no _dispatchChange needed.
  //
  // State vocabulary (matches design spec):
  //   available      — full opacity, transparent border
  //   is-sold-out    — opacity 0.35, solid border; stock absent
  //   is-cross-filtered — opacity 0.28, dashed outline; reversible constraint
  //                    from another option selection

  class SwatchPicker extends BasePicker {
    static selector = '.product-form__input--vaultify-swatch';

    _init() {
      this._group = this.wrap.querySelector('.swatch-group');
      if (!this._group) return;

      this._popup = SwatchPicker._getPopup();
      this._hideTimer = 0;

      this._attachHover();
      this._applyDiscBorders();
      this._syncStates();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsubVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, () => this._syncStates());
      }
      this._onInfoLoaded = () => this._syncStates();
      document.addEventListener('product-info:loaded', this._onInfoLoaded);

      // Re-sync cross-filter states when this picker's own radio changes.
      // Listener is on the wrap element — GC'd naturally when Dawn replaces variant-selects.innerHTML.
      this.wrap.addEventListener('change', (e) => {
        if (e.target.type === 'radio') this._syncStates();
      });
    }

    _destroy() {
      this._unsubVariantChange?.();
      if (this._onInfoLoaded) document.removeEventListener('product-info:loaded', this._onInfoLoaded);
      if (this._onDocTouch) document.removeEventListener('touchstart', this._onDocTouch);
    }

    _applyDiscBorders() {
      this._group.querySelectorAll('.swatch-item').forEach((item) => {
        const disc = item.querySelector('.swatch-disc');
        if (!disc) return;
        const bg = disc.style.background || disc.style.backgroundColor || '';
        // Use a CSS custom property so hover/selected CSS rules can still override via border-color.
        const border = this._isLightColor(bg) ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.1)';
        disc.style.setProperty('--swatch-disc-border', border);
      });
    }

    _isLightColor(bg) {
      const hex = bg.match(/#([0-9a-f]{6})/i);
      if (hex) {
        const r = parseInt(hex[1].slice(0, 2), 16);
        const g = parseInt(hex[1].slice(2, 4), 16);
        const b = parseInt(hex[1].slice(4, 6), 16);
        return (r * 299 + g * 587 + b * 114) / 1000 > 140;
      }
      const rgb = bg.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (rgb) {
        return (parseInt(rgb[1]) * 299 + parseInt(rgb[2]) * 587 + parseInt(rgb[3]) * 114) / 1000 > 140;
      }
      return false;
    }

    _attachHover() {
      const popup = this._popup;
      // Touch devices synthesize a 'mouseenter' on tap but rarely deliver a
      // matching 'mouseleave' (there's no real pointer to leave with), which
      // left the popup stuck open indefinitely after a single tap. Detect
      // real hover capability so touch devices get an explicit dismiss path
      // instead of relying on mouseleave.
      this._supportsHover = !!(window.matchMedia && window.matchMedia('(hover: hover)').matches);

      this._group.querySelectorAll('.swatch-item').forEach((item) => {
        item.addEventListener('mouseenter', () => this._showPopup(item));
        item.addEventListener('mouseleave', () => this._scheduleHide());
      });
      popup.addEventListener('mouseenter', () => clearTimeout(this._hideTimer));
      popup.addEventListener('mouseleave', () => this._scheduleHide());

      if (!this._supportsHover) {
        // Dismiss on the next tap anywhere outside the swatch/popup itself.
        this._onDocTouch = (e) => {
          if (!popup.classList.contains('is-visible')) return;
          if (e.target.closest('.swatch-item') || e.target.closest('.swatch-hover-popup')) return;
          this._scheduleHide(0);
        };
        document.addEventListener('touchstart', this._onDocTouch, { passive: true });
      }
    }

    _syncStates() {
      const product = window.productData;
      if (!product?.variants?.length) return;

      const vs = this.wrap.closest('variant-selects');
      if (!vs) return;

      // Selections array indexed by fieldset DOM order (= option order).
      const allFieldsets = Array.from(vs.querySelectorAll('fieldset'));
      const selections = allFieldsets.map((fs) => {
        const checked = fs.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
        const sel = fs.querySelector('select');
        return sel ? sel.value : '';
      });

      const optIdx = this.optionIdx;

      this._group.querySelectorAll('.swatch-item').forEach((item) => {
        const input = item.querySelector('input[type="radio"]');
        if (!input) return;

        const status = this._swatchStatus(optIdx, input.value, selections, product);
        item.classList.remove('is-sold-out', 'is-cross-filtered');
        input.disabled = false;

        if (status === 'cross-filtered') {
          item.classList.add('is-cross-filtered');
          input.disabled = true;
        } else if (status === 'sold-out') {
          item.classList.add('is-sold-out');
        }
      });
    }

    _swatchStatus(optIdx, value, selections, product) {
      const matching = product.variants.filter((v) => {
        if (v.options[optIdx] !== value) return false;
        for (let i = 0; i < optIdx; i++) {
          if (selections[i] && v.options[i] !== selections[i]) return false;
        }
        return true;
      });
      if (!matching.length) return 'cross-filtered';
      return matching.some((v) => v.available) ? 'available' : 'sold-out';
    }

    _showPopup(item) {
      clearTimeout(this._hideTimer);

      const popup = this._popup;
      const name = item.dataset.swatchName || '';
      const bg = item.dataset.swatchBg || '';
      const discEl = popup.querySelector('.swatch-hover-popup__disc');

      discEl.style.background = bg;
      discEl.style.backgroundSize = bg.includes('url(') ? 'cover' : '';
      discEl.style.backgroundPosition = bg.includes('url(') ? 'center' : '';
      discEl.style.borderColor = this._isLightColor(bg) ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.18)';

      popup.querySelector('.swatch-hover-popup__name').textContent = name;
      popup.querySelector('.swatch-hover-popup__price').textContent = '';

      const stateEl = popup.querySelector('.swatch-hover-popup__state');
      stateEl.textContent = '';
      stateEl.className = 'swatch-hover-popup__state';

      const cents = this._getPriceCents(item.querySelector('input[type="radio"]')?.value);
      if (cents !== null) popup.querySelector('.swatch-hover-popup__price').textContent = this._formatPrice(cents);

      if (item.classList.contains('is-sold-out')) {
        stateEl.classList.add('is-sold-out');
        stateEl.textContent = 'Sold out';
      } else if (item.classList.contains('is-cross-filtered')) {
        stateEl.textContent = 'Not available';
      }

      const disc = item.querySelector('.swatch-disc');
      const rect = disc.getBoundingClientRect();
      popup.style.left = rect.left + rect.width / 2 + 'px';
      popup.style.top = rect.top + 'px';
      popup.classList.add('is-visible');

      // Touch safety net: even with the outside-tap dismiss listener in
      // _attachHover(), auto-hide after a few seconds so the popup can
      // never get stuck open indefinitely on a device with no hover.
      if (!this._supportsHover) {
        this._scheduleHide(3000);
      }
    }

    _scheduleHide(delay = 150) {
      clearTimeout(this._hideTimer);
      this._hideTimer = setTimeout(() => {
        this._popup.classList.remove('is-visible');
      }, delay);
    }

    // Singleton popup — one element shared by all swatch pickers on the page.
    static _getPopup() {
      const existing = document.getElementById('vp-swatch-popup');
      if (existing) return existing;
      const popup = document.createElement('div');
      popup.id = 'vp-swatch-popup';
      popup.className = 'swatch-hover-popup';
      popup.setAttribute('aria-hidden', 'true');
      popup.innerHTML =
        '<span class="swatch-hover-popup__disc"></span>' +
        '<span class="swatch-hover-popup__name"></span>' +
        '<span class="swatch-hover-popup__price"></span>' +
        '<span class="swatch-hover-popup__state"></span>';
      document.body.appendChild(popup);
      return popup;
    }
  }

  VaultifyPickers.register('swatch', SwatchPicker);

  // ── Boot ───────────────────────────────────────────────────────────────────

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      // Frame 1: re-run option visibility filtering on the fresh innerHTML.
      // Dispatching product-info:loaded triggers syncVariantVisibility (which subscribes
      // to that document event because pubsub wasn't loaded at its parse time).
      // updateVariantOptionStates disables sold-out option buttons after each fetch.
      requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('product-info:loaded'));
        document.querySelectorAll('product-info').forEach((pi) => {
          if (typeof pi.updateVariantOptionStates === 'function') pi.updateVariantOptionStates();
        });
      });
      // Reinit pickers + orchestrators. Orchestrators defer initSteps via double-rAF
      // so step state restores after Frame 1 filtering has settled.
      VaultifyPickers.reinit();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => VaultifyPickers.init());
  } else {
    VaultifyPickers.init();
  }

  // Quick-add modal: VaultifyPickers.init() only runs on DOMContentLoaded and variantChange —
  // neither fires when the modal injects a fresh product-info. Listen for the product-info:loaded
  // event that product-info.connectedCallback() dispatches. dataset.originalSection is set
  // exclusively by QuickAddModal.preventDuplicatedIDs(), so this only fires for modal products.
  document.addEventListener('product-info:loaded', (event) => {
    const pi = event.target;
    if (!pi?.dataset?.originalSection) return;
    // connectedCallback fires before children are parsed into the DOM. Defer by one
    // rAF so picker selectors find their elements when init runs.
    requestAnimationFrame(() => VaultifyPickers.init(pi));
  });
})();
